// FrameSight.jsx � Cinematic Video Intelligence
// -----------------------------------------------------------------------------
// "Upload a video. Understand everything."
//
// 12 tabs � deeper, denser, more cinematic than anything VideoLab does:
//
//   UPLOAD       ? drag/drop any video, instant metadata + waveform decode
//   TIMELINE     ? unified intelligence strip: brightness�motion�faces�cuts�speech�emotion
//   COMPOSITION  ? rule-of-thirds heatmap, subject positioning, framing analysis
//   CUTS         ? shot boundary detection, shot type classify, pacing/rhythm analysis
//   FACES        ? face detection timeline, screen time per region, gaze estimation
//   OBJECTS      ? COCO-SSD tracking across frames, object co-occurrence matrix
//   COLOR GRADE  ? cinematic palette, LUT fingerprint, warmth/coolness arc, grading style
//   AUDIO SYNC   ? waveform+video sync, speech detection, music vs speech, beat detection
//   QUALITY      ? blur/focus score, exposure clipping, noise level, stabilisation score
//   ENGAGEMENT   ? predicted viewer engagement curve from visual dynamics
//   COMPARE      ? A/B two segments side-by-side on all metrics
//   REPORT       ? full cinematic brief, director's analytics, export MD/PDF
//
// Engine: 100% browser � Canvas API + manual DSP + TF.js COCO-SSD/BlazeFace
// No video ever leaves the device.
// -----------------------------------------------------------------------------

import React, {
  useState, useEffect, useCallback, useRef, useMemo, useReducer,
} from 'react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Cell, ReferenceLine, ScatterChart, Scatter, RadarChart,
  Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Legend,
} from 'recharts';
import { C, PALETTE, Btn, Label, ChartTip } from './wsShared';
import { requireAuthAction } from '../../utils/authUi';
import { startUsageSession, usageDone, usageStep, usageWorkflowFromTabs } from '../../utils/usageTracker';

// -----------------------------------------------------------------------------
// -- 01  CDN LOADERS ----------------------------------------------------------
// -----------------------------------------------------------------------------

let _tfPromise = null, _cocoPromise = null, _blazePromise = null;

function loadTF() {
  if (_tfPromise) return _tfPromise;
  _tfPromise = new Promise((res, rej) => {
    if (window.tf) { res(window.tf); return; }
    const s = document.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/npm/@tensorflow/tfjs@4.10.0/dist/tf.min.js';
    s.onload = () => setTimeout(() => res(window.tf), 200);
    s.onerror = () => rej(new Error('TensorFlow.js CDN failed'));
    document.head.appendChild(s);
  });
  return _tfPromise;
}

function loadCocoSsd() {
  if (_cocoPromise) return _cocoPromise;
  _cocoPromise = loadTF().then(() => new Promise((res, rej) => {
    if (window.cocoSsd) { res(window.cocoSsd); return; }
    const s = document.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/npm/@tensorflow-models/coco-ssd@2.2.2/dist/coco-ssd.min.js';
    s.onload = () => setTimeout(() => res(window.cocoSsd), 200);
    s.onerror = () => rej(new Error('COCO-SSD CDN failed'));
    document.head.appendChild(s);
  }));
  return _cocoPromise;
}

function loadBlazeFace() {
  if (_blazePromise) return _blazePromise;
  _blazePromise = loadTF().then(() => new Promise((res, rej) => {
    if (window.blazeface) { res(window.blazeface); return; }
    const s = document.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/npm/@tensorflow-models/blazeface@0.0.7/dist/blazeface.min.js';
    s.onload = () => setTimeout(() => res(window.blazeface), 200);
    s.onerror = () => rej(new Error('BlazeFace CDN failed'));
    document.head.appendChild(s);
  }));
  return _blazePromise;
}

// -----------------------------------------------------------------------------
// -- 02  CORE VIDEO ENGINE ----------------------------------------------------
// -----------------------------------------------------------------------------

// Seek video to time and capture canvas frame
function captureFrame(videoEl, time, maxDim = 480) {
  return new Promise(res => {
    const snap = () => {
      const W0 = videoEl.videoWidth, H0 = videoEl.videoHeight;
      const scale = Math.min(1, maxDim / Math.max(W0, H0, 1));
      const W = Math.max(1, Math.round(W0 * scale));
      const H = Math.max(1, Math.round(H0 * scale));
      const c = document.createElement('canvas'); c.width = W; c.height = H;
      const ctx = c.getContext('2d'); ctx.drawImage(videoEl, 0, 0, W, H);
      const id = ctx.getImageData(0, 0, W, H);
      res({ url: c.toDataURL('image/jpeg', 0.82), imgData: id, canvas: c, time, W, H, ctx });
    };
    if (Math.abs(videoEl.currentTime - time) < 0.04) { snap(); return; }
    const onS = () => { videoEl.removeEventListener('seeked', onS); snap(); };
    videoEl.addEventListener('seeked', onS);
    videoEl.currentTime = time;
  });
}

// Extract N frames evenly across duration
async function extractFrames(videoEl, n, onProg) {
  const dur = videoEl.duration;
  const out = [];
  for (let i = 0; i < n; i++) {
    const t = i === 0 ? 0.01 : i === n - 1 ? dur - 0.01 : (i + 0.5) / n * dur;
    out.push(await captureFrame(videoEl, t, 480));
    onProg?.(Math.round((i + 1) / n * 100));
    await new Promise(r => setTimeout(r, 0));
  }
  return out;
}

// --- PER-FRAME METRICS -------------------------------------------------------

function luma(r, g, b) { return 0.2126 * r + 0.7152 * g + 0.0722 * b; }

function frameMetrics(imgData, prevImgData = null) {
  const d = imgData.data, N = d.length / 4;

  // Brightness (luma mean)
  let lumSum = 0, lumSq = 0;
  let rSum = 0, gSum = 0, bSum = 0;
  let clipHigh = 0, clipLow = 0;
  let satSum = 0;

  for (let i = 0; i < d.length; i += 4) {
    const r = d[i], g = d[i + 1], b = d[i + 2];
    const l = luma(r, g, b);
    lumSum += l; lumSq += l * l;
    rSum += r; gSum += g; bSum += b;
    if (l > 235) clipHigh++; if (l < 20) clipLow++;

    // Saturation (HSL)
    const max = Math.max(r, g, b) / 255, min = Math.min(r, g, b) / 255;
    const delta = max - min;
    const s = max === 0 ? 0 : delta / max;
    satSum += s;
  }

  const brightness = +(lumSum / N).toFixed(1);
  const contrast   = +(Math.sqrt(lumSq / N - (lumSum / N) ** 2)).toFixed(1);
  const r = +(rSum / N).toFixed(0);
  const g = +(gSum / N).toFixed(0);
  const b = +(bSum / N).toFixed(0);
  const saturation = +(satSum / N * 100).toFixed(1);
  const clippingHigh = +(clipHigh / N * 100).toFixed(1);
  const clippingLow  = +(clipLow  / N * 100).toFixed(1);

  // Warmth = how warm/cool (R-B balance)
  const warmth = +(((r - b) / 255 + 1) / 2 * 100).toFixed(1);

  // Mean color hex
  const hex = '#' + [r, g, b].map(v => Math.min(255, Math.max(0, Math.round(v))).toString(16).padStart(2, '0')).join('');

  // Sharpness via Laplacian variance
  const W = imgData.width || Math.round(Math.sqrt(N)), H = imgData.height || Math.round(N / W);
  let lapVar = 0, lapCount = 0;
  for (let y = 1; y < H - 1; y++) {
    for (let x = 1; x < W - 1; x++) {
      const idx = (y * W + x) * 4;
      const tl = luma(d[idx - W * 4 - 4], d[idx - W * 4 - 3], d[idx - W * 4 - 2]);
      const tc = luma(d[idx - W * 4],     d[idx - W * 4 + 1], d[idx - W * 4 + 2]);
      const bl = luma(d[idx + W * 4 - 4], d[idx + W * 4 - 3], d[idx + W * 4 - 2]);
      const cc = luma(d[idx],             d[idx + 1],          d[idx + 2]);
      const lap = Math.abs(4 * cc - tl - tc - bl - luma(d[idx + 4], d[idx + 5], d[idx + 6]));
      lapVar += lap ** 2; lapCount++;
    }
  }
  const sharpness = lapCount > 0 ? +(lapVar / lapCount).toFixed(0) : 0;

  // Motion vs previous frame (mean absolute pixel diff)
  let motionScore = 0;
  if (prevImgData) {
    const pd = prevImgData.data;
    let diff = 0;
    for (let i = 0; i < d.length; i += 4) {
      diff += Math.abs(d[i] - pd[i]) + Math.abs(d[i + 1] - pd[i + 1]) + Math.abs(d[i + 2] - pd[i + 2]);
    }
    motionScore = +(diff / (N * 3)).toFixed(2);
  }

  // Noise estimation (high-freq variation)
  let noiseSum = 0;
  for (let i = 4; i < d.length - 4; i += 16) {
    noiseSum += Math.abs(d[i] - (d[i - 4] + d[i + 4]) / 2);
  }
  const noiseLevel = +(noiseSum / (N / 4)).toFixed(2);

  return {
    brightness, contrast, r, g, b, saturation, hex, warmth,
    clippingHigh, clippingLow, sharpness, motionScore, noiseLevel,
  };
}

// --- SHOT / SCENE DETECTION --------------------------------------------------

function detectCuts(timeline, threshold = 30) {
  const cuts = [];
  for (let i = 1; i < timeline.length; i++) {
    const brightDiff = Math.abs(timeline[i].brightness - timeline[i - 1].brightness);
    const motionPeak = timeline[i].motionScore;
    const colorDiff  = Math.abs(timeline[i].r - timeline[i - 1].r) + Math.abs(timeline[i].g - timeline[i - 1].g) + Math.abs(timeline[i].b - timeline[i - 1].b);
    const cutScore = brightDiff * 0.4 + colorDiff * 0.3 + motionPeak * 3;
    if (cutScore > threshold) {
      cuts.push({
        t: timeline[i].time,
        idx: i,
        score: +cutScore.toFixed(1),
        type: cutScore > 80 ? 'hard' : cutScore > 50 ? 'jump' : 'soft',
        brightDiff: +brightDiff.toFixed(1),
        colorDiff:  +colorDiff.toFixed(1),
        motionPeak: +motionPeak.toFixed(2),
      });
    }
  }
  return cuts;
}

// Build shot segments from cut points
function buildShots(cuts, dur) {
  const boundaries = [0, ...cuts.map(c => c.t), dur];
  return boundaries.slice(0, -1).map((start, i) => ({
    id: i,
    start,
    end: boundaries[i + 1],
    duration: +(boundaries[i + 1] - start).toFixed(2),
    cutType: cuts[i - 1]?.type || 'start',
  }));
}

// --- RULE-OF-THIRDS GRID -----------------------------------------------------
// Returns a 3�3 grid of region activity from pixel variance
function analyzeComposition(imgData) {
  const W = imgData.width, H = imgData.height;
  const d = imgData.data;
  const cols = 3, rows = 3;
  const grid = Array.from({ length: rows * cols }, () => ({ energy: 0, count: 0 }));

  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const ci = Math.min(cols - 1, Math.floor(x / W * cols));
      const ri = Math.min(rows - 1, Math.floor(y / H * rows));
      const gi = ri * cols + ci;
      const idx = (y * W + x) * 4;
      const l = luma(d[idx], d[idx + 1], d[idx + 2]);
      grid[gi].energy += l; grid[gi].count++;
    }
  }
  return grid.map(g => ({ energy: g.count > 0 ? +(g.energy / g.count).toFixed(1) : 0 }));
}

// --- ENGAGEMENT SCORE --------------------------------------------------------
// Predicted visual engagement from multiple signals
function computeEngagement(frame) {
  const {
    brightness, contrast, saturation, motionScore, sharpness,
    clippingHigh, clippingLow,
  } = frame;

  // Penalize extremes
  const brightScore   = 100 - Math.abs(brightness - 128) / 1.28;
  const contrastScore = Math.min(100, contrast / 0.6);
  const satScore      = Math.min(100, saturation * 1.2);
  const motionBonus   = Math.min(30, motionScore * 2);
  const sharpBonus    = Math.min(20, sharpness / 50);
  const clipPenalty   = Math.min(30, (clippingHigh + clippingLow) * 0.8);

  return Math.max(0, Math.min(100, +(
    brightScore * 0.25 + contrastScore * 0.25 + satScore * 0.2 +
    motionBonus + sharpBonus - clipPenalty
  ).toFixed(1)));
}

// --- CINEMATIC GRADING STYLE -------------------------------------------------
// Classify the visual look from color temp, contrast, saturation
function classifyGrade(metrics) {
  const avgWarmth  = metrics.reduce((a, m) => a + m.warmth, 0) / metrics.length;
  const avgSat     = metrics.reduce((a, m) => a + m.saturation, 0) / metrics.length;
  const avgContrast= metrics.reduce((a, m) => a + m.contrast, 0) / metrics.length;
  const avgBright  = metrics.reduce((a, m) => a + m.brightness, 0) / metrics.length;

  if (avgSat < 20 && avgContrast > 50) return { style: 'Bleach Bypass', color: C.t3, desc: 'Desaturated, high-contrast � gritty, raw, documentary.' };
  if (avgWarmth > 62) return { style: 'Orange-Teal', color: C.orange, desc: 'Warm skin tones against teal shadows � Hollywood blockbuster look.' };
  if (avgWarmth < 38) return { style: 'Cool & Moody', color: C.blue, desc: 'Blue-shifted � thriller, sci-fi, or melancholy aesthetic.' };
  if (avgSat > 60 && avgBright > 140) return { style: 'Vibrant / Commercial', color: C.gold, desc: 'High saturation + brightness � ads, pop, travel content.' };
  if (avgContrast < 20 && avgBright > 160) return { style: 'Airy / Faded', color: C.cyan, desc: 'Soft, lifted shadows � fashion, lifestyle, VSCO-adjacent.' };
  if (avgBright < 80) return { style: 'Dark & Cinematic', color: C.violet, desc: 'Underexposed � noir, horror, prestige drama.' };
  return { style: 'Balanced / Natural', color: C.green, desc: 'Neutral colour science � documentary, vlog, natural light.' };
}

// --- AUDIO EXTRACTION VIA WEB AUDIO API --------------------------------------

async function extractAudioTimeline(file, numPoints = 600) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
    const ab   = await file.arrayBuffer();
    const decoded = await ctx.decodeAudioData(ab);
    const data = decoded.getChannelData(0);
    const step = Math.max(1, Math.floor(data.length / numPoints));
    const out  = [];
    let silenceCount = 0;
    const overallRMS = Math.sqrt(data.reduce((a, v) => a + v * v, 0) / data.length);
    const threshold  = overallRMS * 0.12;

    for (let i = 0; i < numPoints; i++) {
      let rms = 0;
      for (let j = 0; j < step; j++) rms += (data[i * step + j] || 0) ** 2;
      rms = Math.sqrt(rms / step);
      const t = i * step / decoded.sampleRate;
      if (rms < threshold) silenceCount++;
      out.push({ t: +t.toFixed(3), rms: +rms.toFixed(4), voiced: rms > threshold });
    }
    const silenceRatio = +(silenceCount / numPoints * 100).toFixed(1);
    return { timeline: out, duration: decoded.duration, sampleRate: decoded.sampleRate, silenceRatio };
  } catch {
    return null;
  }
}

// --- FORMAT HELPERS ----------------------------------------------------------
function fmtTime(s) {
  if (!s && s !== 0) return '--:--';
  const m = Math.floor(s / 60), sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
}
function fmtSec(s) { return `${s.toFixed(1)}s`; }

// -----------------------------------------------------------------------------
// -- 03  SHARED UI ATOMS ------------------------------------------------------
// -----------------------------------------------------------------------------

const Card = ({ children, style = {} }) => (
  <div style={{ background: C.surface, border: `1px solid ${C.b1}`, borderRadius: 4, overflow: 'hidden', ...style }}>{children}</div>
);
const Head = ({ label, accent = C.cyan, sub, right }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: C.deep, borderBottom: `1px solid ${C.b0}` }}>
    <div>
      <div style={{ fontSize: 9, color: accent, letterSpacing: '.14em', fontWeight: 700, fontFamily: 'JetBrains Mono,monospace' }}>{label}</div>
      {sub && <div style={{ fontSize: 8, color: C.t4, marginTop: 1 }}>{sub}</div>}
    </div>
    {right && <div style={{ flexShrink: 0 }}>{right}</div>}
  </div>
);
const Body = ({ children, style = {} }) => <div style={{ padding: 14, ...style }}>{children}</div>;

const MB = ({ label, value, color = C.cyan, sub }) => (
  <div style={{ background: C.raised, border: `1px solid ${color}18`, borderLeft: `2px solid ${color}`, padding: '9px 12px', borderRadius: 3 }}>
    <div style={{ fontSize: 7, color: C.t4, letterSpacing: '.12em', fontFamily: 'JetBrains Mono,monospace' }}>{label}</div>
    <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 800, fontSize: 20, color, lineHeight: 1, marginTop: 2 }}>{value}</div>
    {sub && <div style={{ fontSize: 8, color: C.t3, marginTop: 2 }}>{sub}</div>}
  </div>
);

const PBar = ({ pct, color = C.cyan, h = 4 }) => (
  <div style={{ height: h, background: C.b1, borderRadius: 2, overflow: 'hidden' }}>
    <div style={{ height: '100%', width: `${Math.min(100, pct || 0)}%`, background: color, borderRadius: 2, transition: 'width .3s' }} />
  </div>
);

const Info = ({ children, color = C.cyan }) => (
  <div style={{ padding: '10px 14px', background: `${color}0e`, border: `1px solid ${color}30`, borderRadius: 3, fontSize: 11, color: C.t2, lineHeight: 1.75 }}>{children}</div>
);

const CutBadge = ({ type }) => {
  const cfg = { hard: [C.red, 'HARD CUT'], jump: [C.orange, 'JUMP CUT'], soft: [C.gold, 'SOFT CUT'], start: [C.t4, 'START'] };
  const [color, label] = cfg[type] || cfg.start;
  return <span style={{ fontSize: 7, padding: '2px 6px', background: `${color}18`, border: `1px solid ${color}44`, borderRadius: 2, color, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{label}</span>;
};

const EngageBadge = ({ score }) => {
  const color = score > 70 ? C.green : score > 45 ? C.gold : C.red;
  const label = score > 70 ? 'HIGH' : score > 45 ? 'MED' : 'LOW';
  return <span style={{ fontSize: 8, padding: '2px 8px', background: `${color}18`, border: `1px solid ${color}44`, borderRadius: 2, color, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{label} {score.toFixed(0)}</span>;
};

// --- VIDEO THUMBNAIL STRIP ---------------------------------------------------
function FilmStrip({ frames, selected, onSelect, height = 52 }) {
  return (
    <div style={{ display: 'flex', gap: 3, overflowX: 'auto', paddingBottom: 4 }}>
      {frames.map((f, i) => (
        <div key={i} onClick={() => onSelect?.(i)}
          style={{ flexShrink: 0, width: height * (16 / 9), height, borderRadius: 2, overflow: 'hidden', border: `2px solid ${selected === i ? C.cyan : 'transparent'}`, cursor: 'pointer', position: 'relative' }}>
          <img src={f.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          <div style={{ position: 'absolute', bottom: 2, left: 2, fontSize: 7, color: '#fff', background: 'rgba(0,0,0,.7)', padding: '1px 4px', borderRadius: 1, fontFamily: 'JetBrains Mono,monospace' }}>{fmtTime(f.time)}</div>
        </div>
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------------
// -- 04  UPLOAD TAB -----------------------------------------------------------
// -----------------------------------------------------------------------------

function UploadTab({ state, dispatch, isAuthenticated }) {
  const { frames, timeline, meta, videoSrc } = state;
  const [isDrag, setIsDrag]   = useState(false);
  const [loading, setLoading] = useState(false);
  const [pct, setPct]         = useState(0);
  const [stage, setStage]     = useState('');
  const [numFrames, setNumFrames] = useState(120);
  const videoRef = useRef();
  const inputRef = useRef();
  const withAuth = useCallback((action) => requireAuthAction(isAuthenticated, action), [isAuthenticated]);

  const processFile = useCallback(async (file) => {
    if (!requireAuthAction(isAuthenticated, null)) return;
    if (!file?.type.startsWith('video/') && !file?.name.match(/\.(mp4|webm|mov|avi|mkv|m4v|ogv)$/i)) {
      alert('Please upload a video file (MP4, WebM, MOV, AVI, MKV�)'); return;
    }
    setLoading(true); setPct(2); setStage('Loading video�');
    dispatch({ type: 'RESET' });

    const src = URL.createObjectURL(file);
    dispatch({ type: 'SET_SRC', src });

    // Wait for video to be ready
    await new Promise(res => {
      const v = document.createElement('video');
      v.src = src; v.preload = 'metadata'; v.muted = true;
      v.onloadedmetadata = () => {
        dispatch({ type: 'SET_META', meta: {
          name: file.name,
          size: +(file.size / 1024 / 1024).toFixed(2),
          duration: v.duration,
          width: v.videoWidth,
          height: v.videoHeight,
          fps: null,
          aspectRatio: +(v.videoWidth / v.videoHeight).toFixed(3),
        }});
        res(v);
      };
      v.onerror = () => res(v);
    });

    // Extract frames using a hidden video element
    const vid = document.createElement('video');
    vid.src = src; vid.muted = true; vid.preload = 'auto';
    await new Promise(res => { vid.oncanplay = res; vid.load(); });

    setPct(10); setStage(`Extracting ${numFrames} frames�`);
    const rawFrames = await extractFrames(vid, numFrames, p => setPct(10 + p * 0.5));

    setStage('Computing per-frame metrics�');
    const tl = rawFrames.map((f, i) => {
      const metrics = frameMetrics(f.imgData, rawFrames[i - 1]?.imgData || null);
      const engagement = computeEngagement(metrics);
      const composition = analyzeComposition(f.imgData);
      return { time: f.time, ...metrics, engagement, composition, url: f.url, W: f.W, H: f.H };
    });
    setPct(70); await new Promise(r => setTimeout(r, 0));

    setStage('Detecting cuts & shots�');
    const cuts  = detectCuts(tl, 28);
    const shots = buildShots(cuts, vid.duration);
    const grade = classifyGrade(tl);
    setPct(80);

    setStage('Extracting audio�');
    const audio = await extractAudioTimeline(file, 600);
    setPct(92);

    setStage('Finalising�');
    dispatch({ type: 'FULL_LOAD', frames: rawFrames, timeline: tl, cuts, shots, grade, audio });
    startUsageSession('video_intelligence');
    usageStep('video_intelligence', 'upload', {
      name: file.name,
      duration_seconds: +(vid.duration || 0).toFixed(2),
      frames: rawFrames.length,
    }, usageWorkflowFromTabs(TABS));
    setLoading(false); setPct(0); setStage('');
  }, [dispatch, numFrames, isAuthenticated]);

  const onDrop = e => { e.preventDefault(); setIsDrag(false); withAuth(() => processFile(e.dataTransfer.files[0])); };

  const dur = meta?.duration || 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Drop zone */}
      <div
        onDragOver={e => { e.preventDefault(); setIsDrag(true); }}
        onDragLeave={() => setIsDrag(false)}
        onDrop={onDrop}
        onClick={() => !loading && !frames.length && withAuth(() => inputRef.current?.click())}
        style={{ border: `2px dashed ${isDrag ? C.cyan : C.b2}`, borderRadius: 6, padding: frames.length ? '16px 20px' : '56px 24px', textAlign: 'center', cursor: loading ? 'wait' : frames.length ? 'default' : 'pointer', background: isDrag ? `${C.cyan}08` : C.deep, transition: 'all .15s' }}>
        <input ref={inputRef} type="file" accept="video/*,.mkv" style={{ display: 'none' }} onChange={e => withAuth(() => processFile(e.target.files[0]))} />
        {loading ? (
          <>
            <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 800, fontSize: 17, color: C.t1, marginBottom: 10 }}>{stage}</div>
            <div style={{ width: 260, margin: '0 auto', marginBottom: 6 }}><PBar pct={pct} color={C.cyan} h={7} /></div>
            <div style={{ fontSize: 10, color: C.t4, fontFamily: 'JetBrains Mono,monospace' }}>{pct}%</div>
          </>
        ) : frames.length ? (
          <>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
              {videoSrc && <video src={videoSrc} controls muted style={{ maxWidth: 480, maxHeight: 270, borderRadius: 4, background: '#000', flex: '0 0 auto' }} />}
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 700, fontSize: 14, color: C.t1, marginBottom: 6 }}>{meta?.name}</div>
                <div style={{ fontSize: 10, color: C.t3, lineHeight: 1.7 }}>
                  {meta?.width}�{meta?.height} � {fmtTime(dur)} � {meta?.size}MB<br />
                  {frames.length} frames � {state.cuts?.length || 0} cuts � {state.shots?.length || 0} shots
                </div>
                <button onClick={() => dispatch({ type: 'RESET' })} style={{ marginTop: 10, padding: '5px 14px', background: 'transparent', border: `1px solid ${C.red}44`, color: C.red, fontSize: 9, borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace' }}>? CLEAR</button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 56, opacity: .2, marginBottom: 12 }}>??</div>
            <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 800, fontSize: 20, color: C.t1, marginBottom: 6 }}>Drop your video here</div>
            <div style={{ fontSize: 11, color: C.t3, marginBottom: 16 }}>MP4 � WebM � MOV � AVI � MKV � M4V � any resolution, any framerate</div>
            <div style={{ fontSize: 10, color: C.t4 }}>100% in-browser � nothing leaves your device</div>
            <div style={{ display:'inline-block', padding:'4px 10px', borderRadius:999, background:`linear-gradient(90deg, ${C.cyan}18, ${C.violet}16)`, border:`1px solid ${C.cyan}33`, color:C.t1, fontSize:10 }}>
              Your data never leaves your machine. 100% on Browser & 100% Privacy.
            </div>
          </>
        )}
      </div>

      {/* Frame count selector (before loading) */}
      {!frames.length && !loading && (
        <Card>
          <Head label="ANALYSIS DEPTH" accent={C.cyan} />
          <Body>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: 9, color: C.t3, fontFamily: 'JetBrains Mono,monospace' }}>FRAMES TO SAMPLE:</span>
              {[
                [60,  'FAST', '~5s'],
                [120, 'BALANCED', '~10s'],
                [240, 'DEEP', '~20s'],
                [480, 'CINEMATIC', '~40s'],
              ].map(([n, label, est]) => (
                <button key={n} onClick={() => setNumFrames(n)}
                  style={{ padding: '6px 14px', border: `1px solid ${numFrames === n ? C.cyan : C.b1}`, background: numFrames === n ? `${C.cyan}15` : 'transparent', color: numFrames === n ? C.cyan : C.t3, fontSize: 9, borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontWeight: numFrames === n ? 700 : 400 }}>
                  {label} <span style={{ opacity: .6 }}>{n}f � {est}</span>
                </button>
              ))}
            </div>
            <div style={{ marginTop: 10, fontSize: 10, color: C.t3, lineHeight: 1.6 }}>More frames = more detail in timeline charts, better cut detection, richer composition data. Processing time scales linearly.</div>
          </Body>
        </Card>
      )}

      {/* Quick stats */}
      {timeline.length > 0 && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 10 }}>
            <MB label="DURATION"    value={fmtTime(dur)}                                      color={C.cyan} />
            <MB label="RESOLUTION"  value={`${meta?.width}�${meta?.height}`}                  color={C.violet} sub={meta?.aspectRatio > 1.7 ? 'Widescreen' : meta?.aspectRatio > 1.3 ? 'Standard' : 'Square/Vertical'} />
            <MB label="CUTS"        value={state.cuts?.length || 0}                           color={C.orange} sub="detected shots" />
            <MB label="AVG BRIGHT"  value={`${(timeline.reduce((a, f) => a + f.brightness, 0) / timeline.length).toFixed(0)}`} color={C.gold} sub="/ 255" />
            <MB label="AVG ENGAGE"  value={`${(timeline.reduce((a, f) => a + f.engagement, 0) / timeline.length).toFixed(0)}/100`} color={state.grade?.color || C.green} sub={state.grade?.style} />
          </div>

          {/* Filmstrip preview */}
          <Card>
            <Head label={`FILMSTRIP � ${frames.length} SAMPLED FRAMES`} accent={C.cyan} />
            <Body style={{ padding: '10px 10px 6px' }}>
              <FilmStrip frames={frames.filter((_, i) => i % Math.max(1, Math.floor(frames.length / 40)) === 0)} height={54} />
            </Body>
          </Card>

          {/* Module overview */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
            {[
              ['??', 'TIMELINE', `${timeline.length} data points � brightness, motion, saturation, engagement, warmth`, C.cyan],
              ['?', 'CUTS & SHOTS', `${state.cuts?.length || 0} cuts detected � ${state.shots?.length || 0} shots � pacing analysis`, C.orange],
              ['?', 'COMPOSITION', 'Rule-of-thirds heatmap � subject weight � framing score', C.violet],
              ['??', 'FACES', 'BlazeFace detection � screen time map � timeline', C.pink],
              ['??', 'OBJECTS', 'COCO-SSD tracking � 80 classes � co-occurrence', C.green],
              ['??', 'COLOR GRADE', `${state.grade?.style || 'Analysing�'} � LUT fingerprint � warmth arc`, C.gold],
              ['??', 'AUDIO SYNC', `${state.audio ? fmtTime(state.audio.duration) + ' � ' + state.audio.silenceRatio + '% silence' : 'Extracted'}`, C.violet],
              ['??', 'QUALITY', 'Sharpness � exposure clipping � noise � stability', C.cyan],
              ['??', 'ENGAGEMENT', 'Predicted viewer attention curve � peak moments', C.green],
            ].map(([ic, t, d, c]) => (
              <div key={t} style={{ padding: '12px 14px', background: C.raised, border: `1px solid ${c}22`, borderLeft: `3px solid ${c}`, borderRadius: 3 }}>
                <div style={{ fontSize: 20, marginBottom: 6, opacity: .5 }}>{ic}</div>
                <div style={{ fontSize: 10, color: c, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, marginBottom: 4 }}>{t}</div>
                <div style={{ fontSize: 9.5, color: C.t3, lineHeight: 1.6 }}>{d}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// -- 05  TIMELINE TAB � unified intelligence strip ----------------------------
// -----------------------------------------------------------------------------

function TimelineTab({ state }) {
  const { timeline, cuts, frames, audio } = state;
  const [layer, setLayer] = useState('all');
  const [hovered, setHovered] = useState(null);

  if (!timeline.length) return <div style={{ padding: 60, textAlign: 'center', color: C.t4 }}><div style={{ fontSize: 36, opacity: .2, marginBottom: 10 }}>??</div>Load a video first.</div>;

  const layers = [
    { id: 'all',        label: 'ALL SIGNALS',  color: C.t2 },
    { id: 'brightness', label: 'BRIGHTNESS',   color: C.gold   },
    { id: 'motion',     label: 'MOTION',       color: C.orange },
    { id: 'saturation', label: 'SATURATION',   color: C.violet },
    { id: 'engagement', label: 'ENGAGEMENT',   color: C.green  },
    { id: 'sharpness',  label: 'SHARPNESS',    color: C.cyan   },
    { id: 'warmth',     label: 'WARMTH',       color: C.orange },
    { id: 'noise',      label: 'NOISE',        color: C.red    },
  ];

  // Normalise motion score 0-100
  const maxMotion = Math.max(...timeline.map(f => f.motionScore), 1);
  const maxSharp  = Math.max(...timeline.map(f => f.sharpness), 1);
  const data = timeline.map(f => ({
    ...f,
    motionPct:   +(f.motionScore / maxMotion * 100).toFixed(1),
    sharpnessPct:+(f.sharpness   / maxSharp  * 100).toFixed(1),
    noisePct:    Math.min(100, +((f.noiseLevel || 0) * 800).toFixed(1)),
  }));

  const CHART_H = layer === 'all' ? 120 : 220;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Layer selector */}
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {layers.map(l => (
          <button key={l.id} onClick={() => setLayer(l.id)} style={{ padding: '5px 12px', border: `1px solid ${layer === l.id ? l.color : C.b0}`, background: layer === l.id ? `${l.color}15` : C.raised, color: layer === l.id ? l.color : C.t3, fontSize: 8, cursor: 'pointer', borderRadius: 3, fontFamily: 'JetBrains Mono,monospace', fontWeight: layer === l.id ? 700 : 400 }}>{l.label}</button>
        ))}
      </div>

      {/* Color strip � actual mean frame color */}
      <Card>
        <Head label="VIDEO COLOR STRIP � actual mean pixel color per frame" accent={C.orange} sub={`${timeline.length} sampled frames`} />
        <Body style={{ padding: '10px 10px 6px' }}>
          <div style={{ display: 'flex', height: 48, borderRadius: 3, overflow: 'hidden', gap: '0.5px', cursor: 'crosshair' }}>
            {timeline.map((f, i) => (
              <div key={i} style={{ flex: 1, background: f.hex, minWidth: 1 }} title={`${fmtTime(f.time)}: ${f.hex}`}
                onMouseEnter={() => setHovered(f)} onMouseLeave={() => setHovered(null)} />
            ))}
          </div>
          {/* Cut markers */}
          {cuts?.length > 0 && (
            <div style={{ position: 'relative', height: 12 }}>
              {cuts.map((c, i) => (
                <div key={i} style={{ position: 'absolute', top: 2, left: `${c.t / timeline[timeline.length - 1].time * 100}%`, width: 1, height: 8, background: c.type === 'hard' ? C.red : c.type === 'jump' ? C.orange : C.gold }} title={`${c.type} cut at ${fmtTime(c.t)}`} />
              ))}
            </div>
          )}
          {hovered && (
            <div style={{ marginTop: 6, fontSize: 9, color: C.t3, fontFamily: 'JetBrains Mono,monospace', display: 'flex', gap: 12 }}>
              <span>{fmtTime(hovered.time)}</span>
              <span style={{ color: hovered.hex }}>� {hovered.hex.toUpperCase()}</span>
              <span>B:{hovered.brightness.toFixed(0)}</span>
              <span>S:{hovered.saturation.toFixed(0)}%</span>
              <span>W:{hovered.warmth.toFixed(0)}%</span>
            </div>
          )}
        </Body>
      </Card>

      {/* Charts */}
      {(layer === 'all' || layer === 'brightness') && (
        <Card>
          <Head label="BRIGHTNESS OVER TIME" accent={C.gold} sub="Luma mean per frame � shows exposure arc, fade in/out, day-night" />
          <Body style={{ padding: 10 }}>
            <ResponsiveContainer width="100%" height={CHART_H}>
              <AreaChart data={data} margin={{ top: 4, right: 10, bottom: 10, left: 0 }}>
                <defs><linearGradient id="bGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.gold} stopOpacity={0.5} /><stop offset="100%" stopColor={C.gold} stopOpacity={0.02} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
                <XAxis dataKey="time" tick={{ fill: C.t4, fontSize: 8 }} tickFormatter={fmtTime} />
                <YAxis tick={{ fill: C.t4, fontSize: 8 }} domain={[0, 255]} />
                <Tooltip content={<ChartTip />} labelFormatter={fmtTime} />
                {cuts?.map((c, i) => <ReferenceLine key={i} x={c.t} stroke={c.type === 'hard' ? C.red : C.orange} strokeDasharray="2 2" strokeWidth={1} />)}
                <Area type="monotone" dataKey="brightness" stroke={C.gold} fill="url(#bGrad)" strokeWidth={1.5} dot={false} isAnimationActive={false} name="Brightness" />
              </AreaChart>
            </ResponsiveContainer>
          </Body>
        </Card>
      )}

      {(layer === 'all' || layer === 'motion') && (
        <Card>
          <Head label="MOTION ENERGY" accent={C.orange} sub="Mean abs pixel delta between consecutive frames � cuts / camera moves / action" />
          <Body style={{ padding: 10 }}>
            <ResponsiveContainer width="100%" height={CHART_H}>
              <AreaChart data={data} margin={{ top: 4, right: 10, bottom: 10, left: 0 }}>
                <defs><linearGradient id="mGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.orange} stopOpacity={0.55} /><stop offset="100%" stopColor={C.orange} stopOpacity={0.02} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
                <XAxis dataKey="time" tick={{ fill: C.t4, fontSize: 8 }} tickFormatter={fmtTime} />
                <YAxis tick={{ fill: C.t4, fontSize: 8 }} />
                <Tooltip content={<ChartTip />} labelFormatter={fmtTime} />
                {cuts?.map((c, i) => <ReferenceLine key={i} x={c.t} stroke={C.red} strokeWidth={1} />)}
                <Area type="monotone" dataKey="motionPct" stroke={C.orange} fill="url(#mGrad)" strokeWidth={1.5} dot={false} isAnimationActive={false} name="Motion %" />
              </AreaChart>
            </ResponsiveContainer>
          </Body>
        </Card>
      )}

      {(layer === 'all' || layer === 'saturation') && (
        <Card>
          <Head label="SATURATION ARC" accent={C.violet} sub="Colour vibrancy over time � narrative intensity, mood transitions" />
          <Body style={{ padding: 10 }}>
            <ResponsiveContainer width="100%" height={CHART_H}>
              <AreaChart data={data} margin={{ top: 4, right: 10, bottom: 10, left: 0 }}>
                <defs><linearGradient id="sGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.violet} stopOpacity={0.4} /><stop offset="100%" stopColor={C.violet} stopOpacity={0.02} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
                <XAxis dataKey="time" tick={{ fill: C.t4, fontSize: 8 }} tickFormatter={fmtTime} />
                <YAxis tick={{ fill: C.t4, fontSize: 8 }} domain={[0, 100]} />
                <Tooltip content={<ChartTip />} labelFormatter={fmtTime} />
                <Area type="monotone" dataKey="saturation" stroke={C.violet} fill="url(#sGrad)" strokeWidth={1.5} dot={false} isAnimationActive={false} name="Saturation %" />
              </AreaChart>
            </ResponsiveContainer>
          </Body>
        </Card>
      )}

      {(layer === 'all' || layer === 'engagement') && (
        <Card>
          <Head label="PREDICTED ENGAGEMENT CURVE" accent={C.green} sub="Composite score: brightness � contrast � saturation � motion � sharpness - clipping" />
          <Body style={{ padding: 10 }}>
            <ResponsiveContainer width="100%" height={CHART_H}>
              <AreaChart data={data} margin={{ top: 4, right: 10, bottom: 10, left: 0 }}>
                <defs><linearGradient id="eGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.green} stopOpacity={0.45} /><stop offset="100%" stopColor={C.green} stopOpacity={0.02} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
                <XAxis dataKey="time" tick={{ fill: C.t4, fontSize: 8 }} tickFormatter={fmtTime} />
                <YAxis tick={{ fill: C.t4, fontSize: 8 }} domain={[0, 100]} />
                <Tooltip content={<ChartTip />} labelFormatter={fmtTime} />
                <ReferenceLine y={70} stroke={C.green} strokeDasharray="4 3" strokeWidth={1} />
                <ReferenceLine y={40} stroke={C.red} strokeDasharray="4 3" strokeWidth={1} />
                <Area type="monotone" dataKey="engagement" stroke={C.green} fill="url(#eGrad)" strokeWidth={2} dot={false} isAnimationActive={false} name="Engagement" />
              </AreaChart>
            </ResponsiveContainer>
            <div style={{ fontSize: 8, color: C.t4, fontFamily: 'JetBrains Mono,monospace', display: 'flex', gap: 14, marginTop: 4 }}>
              <span style={{ color: C.green }}>-- 70 HIGH</span>
              <span style={{ color: C.red }}>-- 40 LOW</span>
              <span>Avg: {(data.reduce((a, d) => a + d.engagement, 0) / data.length).toFixed(1)}</span>
            </div>
          </Body>
        </Card>
      )}

      {(layer === 'all' || layer === 'warmth') && (
        <Card>
          <Head label="COLOUR TEMPERATURE ARC � warm ? cool" accent={C.orange} sub="(R-B balance) � shows narrative warmth shift, time of day, grading intent" />
          <Body style={{ padding: 10 }}>
            <ResponsiveContainer width="100%" height={CHART_H}>
              <AreaChart data={data} margin={{ top: 4, right: 10, bottom: 10, left: 0 }}>
                <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
                <XAxis dataKey="time" tick={{ fill: C.t4, fontSize: 8 }} tickFormatter={fmtTime} />
                <YAxis tick={{ fill: C.t4, fontSize: 8 }} domain={[0, 100]} />
                <Tooltip content={<ChartTip />} labelFormatter={fmtTime} />
                <ReferenceLine y={50} stroke={C.t4} strokeDasharray="3 3" />
                <Area type="monotone" dataKey="warmth" stroke={C.orange} fill={C.orange} fillOpacity={0.2} strokeWidth={1.5} dot={false} isAnimationActive={false} name="Warmth %" />
              </AreaChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 8, color: C.t4, fontFamily: 'JetBrains Mono,monospace', marginTop: 4 }}><span style={{ color: C.blue }}>? COOL</span><span>NEUTRAL 50</span><span style={{ color: C.orange }}>WARM ?</span></div>
          </Body>
        </Card>
      )}

      {(layer === 'sharpness' || layer === 'noise') && (
        <Card>
          <Head label={layer === 'sharpness' ? 'FOCUS / SHARPNESS' : 'NOISE LEVEL'} accent={layer === 'sharpness' ? C.cyan : C.red} />
          <Body style={{ padding: 10 }}>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={data} margin={{ top: 4, right: 10, bottom: 10, left: 0 }}>
                <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
                <XAxis dataKey="time" tick={{ fill: C.t4, fontSize: 8 }} tickFormatter={fmtTime} />
                <YAxis tick={{ fill: C.t4, fontSize: 8 }} />
                <Tooltip content={<ChartTip />} labelFormatter={fmtTime} />
                <Area type="monotone" dataKey={layer === 'sharpness' ? 'sharpnessPct' : 'noisePct'} stroke={layer === 'sharpness' ? C.cyan : C.red} fill={layer === 'sharpness' ? C.cyan : C.red} fillOpacity={0.2} strokeWidth={1.5} dot={false} isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          </Body>
        </Card>
      )}

      {/* Audio overlay if available */}
      {(layer === 'all') && state.audio?.timeline && (
        <Card>
          <Head label="AUDIO ENERGY" accent={C.violet} sub="RMS amplitude over time � dark gaps = silence" />
          <Body style={{ padding: 10 }}>
            <ResponsiveContainer width="100%" height={80}>
              <AreaChart data={state.audio.timeline} margin={{ top: 2, right: 10, bottom: 0, left: 0 }}>
                <Area type="monotone" dataKey="rms" stroke={C.violet} fill={C.violet} fillOpacity={0.3} strokeWidth={1} dot={false} isAnimationActive={false} />
                <XAxis dataKey="t" hide />
                <YAxis hide />
              </AreaChart>
            </ResponsiveContainer>
          </Body>
        </Card>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// -- 06  COMPOSITION TAB ------------------------------------------------------
// -----------------------------------------------------------------------------

function CompositionTab({ state }) {
  const { timeline, frames } = state;
  const [selIdx, setSelIdx] = useState(0);

  // Aggregate composition grid across all frames
  const avgGrid = useMemo(() => {
    if (!timeline.length) return Array(9).fill({ energy: 0 });
    const sums = Array(9).fill(0);
    timeline.forEach(f => f.composition?.forEach((c, i) => { sums[i] += c.energy; }));
    return sums.map(s => ({ energy: +(s / timeline.length).toFixed(1) }));
  }, [timeline]);

  const maxEnergy = Math.max(...avgGrid.map(g => g.energy), 1);
  const ROT_NAMES = ['Top-Left','Top-Center','Top-Right','Mid-Left','CENTER','Mid-Right','Bot-Left','Bot-Center','Bot-Right'];
  const ROT_THIRDS = [0, 2, 6, 8]; // corner intersections

  const frame = frames[selIdx];
  const frameGrid = frame ? (timeline[selIdx]?.composition || avgGrid) : avgGrid;

  // Dominant region analysis
  const sorted = avgGrid.map((g, i) => ({ ...g, i, name: ROT_NAMES[i] })).sort((a, b) => b.energy - a.energy);
  const dominant = sorted[0];
  const isThirdPoint = ROT_THIRDS.includes(dominant?.i);

  // Framing score: ideally thirds points should have high energy
  const thirdsScore = Math.round(
    ROT_THIRDS.reduce((a, idx) => a + avgGrid[idx].energy, 0) /
    ROT_THIRDS.reduce((a, _, j) => a + (j < 4 ? 1 : 0), 4) / maxEnergy * 100
  );

  if (!timeline.length) return <div style={{ padding: 60, textAlign: 'center', color: C.t4 }}><div style={{ fontSize: 36, opacity: .2 }}>?</div>Load a video first.</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
        <MB label="RULE-OF-THIRDS" value={`${thirdsScore}/100`} color={thirdsScore > 65 ? C.green : thirdsScore > 40 ? C.gold : C.red} sub="framing quality" />
        <MB label="DOMINANT ZONE"  value={dominant?.name || '�'}                color={C.cyan}   sub="highest avg energy" />
        <MB label="CENTER WEIGHT"  value={`${(avgGrid[4].energy / maxEnergy * 100).toFixed(0)}%`} color={C.violet} sub="centered composition" />
        <MB label="CORNER BALANCE" value={(() => { const c = [0,2,6,8]; const v = c.map(i=>avgGrid[i].energy); const avg=v.reduce((a,b)=>a+b,0)/4; const std=Math.sqrt(v.reduce((a,b)=>a+(b-avg)**2,0)/4); return std < avg * 0.3 ? 'BALANCED' : 'ASYMMETRIC'; })()} color={C.gold} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        {/* Rule of thirds heatmap (average across video) */}
        <Card>
          <Head label="AVERAGE COMPOSITION HEATMAP" accent={C.violet} sub="Which screen regions are brightest on average � 3�3 grid" />
          <Body>
            {/* 3x3 grid */}
            <div style={{ position: 'relative', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, marginBottom: 14, background: C.deep, padding: 8, borderRadius: 4 }}>
              {/* Rule-of-thirds overlay lines */}
              {avgGrid.map((cell, i) => {
                const heat = cell.energy / maxEnergy;
                const isThird = ROT_THIRDS.includes(i);
                return (
                  <div key={i} style={{ height: 80, borderRadius: 3, background: `rgba(${Math.round(heat * 100)},${Math.round(heat * 200)},${Math.round(heat * 255)},${0.2 + heat * 0.7})`, border: `2px solid ${isThird ? C.cyan + '60' : C.b1}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', position: 'relative' }}>
                    <div style={{ fontSize: 14, fontFamily: 'Syne,sans-serif', fontWeight: 800, color: `rgba(255,255,255,${0.5 + heat * 0.5})` }}>{cell.energy.toFixed(0)}</div>
                    {isThird && <div style={{ fontSize: 6, color: C.cyan, fontFamily: 'JetBrains Mono,monospace', marginTop: 2 }}>? THIRDS</div>}
                    <div style={{ fontSize: 7, color: 'rgba(255,255,255,.5)', fontFamily: 'JetBrains Mono,monospace', marginTop: 2 }}>{ROT_NAMES[i].split('-')[1] || ROT_NAMES[i]}</div>
                  </div>
                );
              })}
            </div>
            <div style={{ fontSize: 9, color: C.t3, lineHeight: 1.65 }}>
              ? marks rule-of-thirds intersection points. Good composition places subjects at these intersections. <span style={{ color: thirdsScore > 65 ? C.green : C.gold }}>Score: {thirdsScore}/100</span>
            </div>
          </Body>
        </Card>

        {/* Per-frame composition */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Card>
            <Head label="FRAME COMPOSITION DETAIL" accent={C.violet} sub="Select frame below" />
            <Body style={{ padding: 10 }}>
              {frame && <img src={frame.url} alt="" style={{ width: '100%', maxHeight: 200, objectFit: 'contain', display: 'block', borderRadius: 3, marginBottom: 10 }} />}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                {frameGrid.map((cell, i) => {
                  const heat = cell.energy / maxEnergy;
                  const isThird = ROT_THIRDS.includes(i);
                  return (
                    <div key={i} style={{ height: 40, borderRadius: 2, background: `rgba(168,100,255,${0.1 + heat * 0.7})`, border: `1px solid ${isThird ? C.cyan + '80' : C.b0}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: 'rgba(255,255,255,.8)', fontFamily: 'Syne,sans-serif', fontWeight: 700 }}>
                      {cell.energy.toFixed(0)}
                    </div>
                  );
                })}
              </div>
            </Body>
          </Card>
          <FilmStrip frames={frames.filter((_, i) => i % Math.max(1, Math.floor(frames.length / 20)) === 0)} selected={selIdx} onSelect={setSelIdx} height={46} />
        </div>
      </div>

      {/* Composition over time */}
      <Card>
        <Head label="CENTER vs THIRDS ENERGY OVER TIME" accent={C.violet} sub="Tracks whether subjects stay in rule-of-thirds positions across the edit" />
        <Body style={{ padding: 10 }}>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={timeline} margin={{ top: 4, right: 10, bottom: 10, left: 0 }}>
              <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
              <XAxis dataKey="time" tick={{ fill: C.t4, fontSize: 8 }} tickFormatter={fmtTime} />
              <YAxis tick={{ fill: C.t4, fontSize: 8 }} domain={[0, 255]} />
              <Tooltip content={<ChartTip />} labelFormatter={fmtTime} />
              <Line type="monotone" dataKey={f => f.composition?.[4]?.energy ?? 0} stroke={C.violet} dot={false} strokeWidth={1.5} isAnimationActive={false} name="Center" />
              <Line type="monotone" dataKey={f => ((f.composition?.[0]?.energy || 0) + (f.composition?.[2]?.energy || 0) + (f.composition?.[6]?.energy || 0) + (f.composition?.[8]?.energy || 0)) / 4} stroke={C.cyan} dot={false} strokeWidth={1.5} isAnimationActive={false} name="Thirds avg" />
              <Legend wrapperStyle={{ fontSize: 9 }} />
            </LineChart>
          </ResponsiveContainer>
        </Body>
      </Card>

      {/* Region breakdown bars */}
      <Card>
        <Head label="REGION ENERGY RANKING" accent={C.violet} />
        <Body>
          {sorted.map((zone, i) => (
            <div key={zone.i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <div style={{ fontSize: 9, color: C.t3, fontFamily: 'JetBrains Mono,monospace', minWidth: 90 }}>{zone.name}</div>
              <div style={{ flex: 1 }}><PBar pct={zone.energy / maxEnergy * 100} color={ROT_THIRDS.includes(zone.i) ? C.cyan : C.violet} h={8} /></div>
              <div style={{ fontSize: 10, fontFamily: 'Syne,sans-serif', fontWeight: 700, color: ROT_THIRDS.includes(zone.i) ? C.cyan : C.t1, minWidth: 36, textAlign: 'right' }}>{zone.energy.toFixed(0)}</div>
              {ROT_THIRDS.includes(zone.i) && <span style={{ fontSize: 7, color: C.cyan, fontFamily: 'JetBrains Mono,monospace' }}>?</span>}
            </div>
          ))}
        </Body>
      </Card>
    </div>
  );
}

// -----------------------------------------------------------------------------
// -- 07  CUTS TAB -------------------------------------------------------------
// -----------------------------------------------------------------------------

function CutsTab({ state }) {
  const { cuts, shots, timeline, frames, meta } = state;
  const [selShot, setSelShot] = useState(null);

  if (!cuts) return <div style={{ padding: 60, textAlign: 'center', color: C.t4 }}><div style={{ fontSize: 36, opacity: .2 }}>?</div>Load a video first.</div>;

  const dur = meta?.duration || 1;
  const avgShotDur = shots?.length ? shots.reduce((a, s) => a + s.duration, 0) / shots.length : 0;
  const shortShots = shots?.filter(s => s.duration < 2).length || 0;
  const longShots  = shots?.filter(s => s.duration > 10).length || 0;

  const cutTypes = cuts.reduce((a, c) => { a[c.type] = (a[c.type] || 0) + 1; return a; }, {});
  const pacingLabel = avgShotDur < 2 ? 'RAPID FIRE' : avgShotDur < 5 ? 'DYNAMIC' : avgShotDur < 15 ? 'MODERATE' : 'SLOW / CINEMATIC';

  // Rhythm: inter-cut intervals
  const intervals = cuts.map((c, i) => i > 0 ? c.t - cuts[i - 1].t : c.t);
  const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length || 0;
  const intervalStd = Math.sqrt(intervals.reduce((a, b) => a + (b - avgInterval) ** 2, 0) / intervals.length) || 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 10 }}>
        <MB label="TOTAL CUTS"    value={cuts.length}            color={C.orange} />
        <MB label="SHOTS"         value={shots?.length || 0}     color={C.gold}   />
        <MB label="AVG SHOT DUR"  value={`${avgShotDur.toFixed(1)}s`} color={C.cyan} sub={pacingLabel} />
        <MB label="SHORT SHOTS"   value={shortShots}             color={C.red}    sub="< 2s" />
        <MB label="LONG SHOTS"    value={longShots}              color={C.green}  sub="> 10s" />
      </div>

      {/* Pacing style */}
      <Card>
        <Head label="EDIT PACING PROFILE" accent={C.orange} />
        <Body>
          <div style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 32, fontFamily: 'Syne,sans-serif', fontWeight: 900, color: C.orange }}>{pacingLabel}</div>
              <div style={{ fontSize: 11, color: C.t2, marginTop: 4 }}>avg shot: {avgShotDur.toFixed(1)}s � rhythm std: {intervalStd.toFixed(1)}s</div>
            </div>
            <div style={{ flex: 1, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {[
                ['HARD CUTS', cutTypes.hard || 0, C.red],
                ['JUMP CUTS', cutTypes.jump || 0, C.orange],
                ['SOFT CUTS', cutTypes.soft || 0, C.gold],
              ].map(([l, v, c]) => (
                <div key={l} style={{ padding: '10px 16px', background: `${c}12`, border: `1px solid ${c}44`, borderRadius: 4, textAlign: 'center' }}>
                  <div style={{ fontSize: 22, fontFamily: 'Syne,sans-serif', fontWeight: 800, color: c }}>{v}</div>
                  <div style={{ fontSize: 8, color: c, fontFamily: 'JetBrains Mono,monospace', marginTop: 3 }}>{l}</div>
                </div>
              ))}
            </div>
          </div>
        </Body>
      </Card>

      {/* Shot timeline visual */}
      <Card>
        <Head label="SHOT TIMELINE � click shot to inspect" accent={C.orange} sub="Width = duration � colour = cut type" />
        <Body style={{ padding: 12 }}>
          <div style={{ display: 'flex', height: 56, background: C.deep, borderRadius: 3, overflow: 'hidden', marginBottom: 6, cursor: 'pointer' }}>
            {shots?.map(shot => {
              const w = shot.duration / dur * 100;
              const color = shot.cutType === 'hard' ? C.red : shot.cutType === 'jump' ? C.orange : shot.cutType === 'soft' ? C.gold : C.t4;
              const frame = frames[Math.max(0, Math.round(shot.start / dur * frames.length))];
              return (
                <div key={shot.id} title={`Shot ${shot.id + 1}: ${fmtTime(shot.start)} ? ${fmtTime(shot.end)} (${shot.duration.toFixed(1)}s)`}
                  onClick={() => setSelShot(selShot?.id === shot.id ? null : shot)}
                  style={{ width: `${w}%`, minWidth: 2, flexShrink: 0, position: 'relative', borderRight: `1px solid ${color}`, overflow: 'hidden', transition: 'opacity .1s', opacity: selShot && selShot.id !== shot.id ? 0.5 : 1 }}>
                  {frame && w > 3 && <img src={frame.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: .7 }} />}
                  <div style={{ position: 'absolute', bottom: 2, left: 2, fontSize: 6, color: '#fff', fontFamily: 'JetBrains Mono,monospace', background: 'rgba(0,0,0,.6)', padding: '1px 3px', borderRadius: 1, whiteSpace: 'nowrap' }}>
                    {w > 5 ? `${shot.duration.toFixed(0)}s` : ''}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Legend */}
          <div style={{ display: 'flex', gap: 14, fontSize: 8, color: C.t4, fontFamily: 'JetBrains Mono,monospace' }}>
            {[['HARD', C.red], ['JUMP', C.orange], ['SOFT', C.gold]].map(([l, c]) => <span key={l} style={{ color: c }}>?{l} CUT</span>)}
          </div>
        </Body>
      </Card>

      {/* Selected shot detail */}
      {selShot && (
        <Card style={{ borderTop: `3px solid ${C.orange}` }}>
          <Head label={`SHOT ${selShot.id + 1} � ${fmtTime(selShot.start)} ? ${fmtTime(selShot.end)}`} accent={C.orange} sub={`${selShot.duration.toFixed(2)}s � ${selShot.cutType} cut`} />
          <Body>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              {[selShot.start, (selShot.start + selShot.end) / 2, selShot.end - 0.05].map((t, i) => {
                const fi = Math.round(t / dur * frames.length);
                const frame = frames[Math.min(frames.length - 1, fi)];
                return frame && (
                  <div key={i}><div style={{ fontSize: 8, color: C.t4, fontFamily: 'JetBrains Mono,monospace', marginBottom: 4 }}>{i === 0 ? 'IN' : i === 1 ? 'MID' : 'OUT'} � {fmtTime(t)}</div>
                    <img src={frame.url} alt="" style={{ width: '100%', borderRadius: 3 }} />
                  </div>
                );
              })}
            </div>
          </Body>
        </Card>
      )}

      {/* Cut list */}
      <Card>
        <Head label="CUT DETECTION TABLE" accent={C.orange} sub={`${cuts.length} cuts � threshold = 28 composite score`} />
        <Body style={{ maxHeight: 380, overflowY: 'auto', padding: 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: C.deep }}>
              {['#', 'TIME', 'TYPE', 'SCORE', 'BRIGHT ?', 'COLOR ?', 'MOTION', 'SHOT DUR'].map(h => (
                <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontSize: 8, color: C.t3, fontFamily: 'JetBrains Mono,monospace', borderBottom: `1px solid ${C.b1}`, whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {cuts.map((c, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${C.b0}`, background: i % 2 === 0 ? 'transparent' : `${C.raised}60` }}>
                  <td style={{ padding: '5px 10px', fontSize: 9, color: C.t4, fontFamily: 'JetBrains Mono,monospace' }}>{i + 1}</td>
                  <td style={{ padding: '5px 10px', fontSize: 9, color: C.cyan, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{fmtTime(c.t)}</td>
                  <td style={{ padding: '5px 10px' }}><CutBadge type={c.type} /></td>
                  <td style={{ padding: '5px 10px', fontSize: 9, color: C.t1, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{c.score.toFixed(1)}</td>
                  <td style={{ padding: '5px 10px', fontSize: 9, color: C.gold, fontFamily: 'JetBrains Mono,monospace' }}>{c.brightDiff.toFixed(1)}</td>
                  <td style={{ padding: '5px 10px', fontSize: 9, color: C.violet, fontFamily: 'JetBrains Mono,monospace' }}>{c.colorDiff.toFixed(1)}</td>
                  <td style={{ padding: '5px 10px', fontSize: 9, color: C.orange, fontFamily: 'JetBrains Mono,monospace' }}>{c.motionPeak.toFixed(2)}</td>
                  <td style={{ padding: '5px 10px', fontSize: 9, color: C.t2, fontFamily: 'JetBrains Mono,monospace' }}>{shots?.[i + 1] ? `${shots[i + 1].duration.toFixed(1)}s` : '�'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Body>
      </Card>

      {/* Inter-cut interval distribution */}
      <Card>
        <Head label="INTER-CUT INTERVAL DISTRIBUTION � edit rhythm" accent={C.orange} />
        <Body style={{ padding: 10 }}>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={(() => {
              const bins = Array.from({ length: 20 }, (_, i) => ({ bin: `${(i * 2).toFixed(0)}s`, count: 0 }));
              intervals.forEach(iv => { const bi = Math.min(19, Math.floor(iv / 2)); bins[bi].count++; });
              return bins.filter(b => b.count > 0);
            })()} barCategoryGap="15%">
              <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
              <XAxis dataKey="bin" tick={{ fill: C.t4, fontSize: 8 }} />
              <YAxis tick={{ fill: C.t4, fontSize: 8 }} />
              <Tooltip content={<ChartTip />} />
              <Bar dataKey="count" fill={C.orange} radius={[3, 3, 0, 0]} opacity={0.85} />
            </BarChart>
          </ResponsiveContainer>
        </Body>
      </Card>
    </div>
  );
}

// -----------------------------------------------------------------------------
// -- 08  FACES TAB � BlazeFace detection + screen-time heatmap ----------------
// -----------------------------------------------------------------------------

function FacesTab({ state, dispatch }) {
  const { frames, timeline, meta, faceData } = state;
  const [running, setRunning]   = useState(false);
  const [progress, setProgress] = useState(0);
  const [selIdx, setSelIdx]     = useState(0);
  const [error, setError]       = useState(null);

  const runDetection = useCallback(async () => {
    if (!frames.length) return;
    setRunning(true); setError(null);
    try {
      setProgress(5);
      const bf = await loadBlazeFace();
      if (!bf) throw new Error('BlazeFace CDN failed');
      setProgress(15);
      const model = await bf.load();
      setProgress(25);

      const results = [];
      const sampleFrames = frames.filter((_, i) => i % Math.max(1, Math.floor(frames.length / 60)) === 0);
      for (let i = 0; i < sampleFrames.length; i++) {
        const f = sampleFrames[i];
        const img = new Image(); img.src = f.url;
        await new Promise(r => { img.onload = r; if (img.complete) r(); });
        try {
          const predictions = await model.estimateFaces(img, false);
          results.push({ time: f.time, faces: predictions.map(p => ({
            topLeft:     [p.topLeft[0]     / f.W, p.topLeft[1]     / f.H],
            bottomRight: [p.bottomRight[0] / f.W, p.bottomRight[1] / f.H],
            probability: +(p.probability?.[0] ?? 0.9).toFixed(3),
          })) });
        } catch {
          results.push({ time: f.time, faces: [] });
        }
        setProgress(25 + Math.round(i / sampleFrames.length * 70));
        await new Promise(r => setTimeout(r, 0));
      }

      dispatch({ type: 'FACE_DATA', faceData: results });
      setProgress(100);
      setTimeout(() => { setRunning(false); setProgress(0); }, 500);
    } catch (e) {
      setError(`BlazeFace: ${e.message}`);
      setRunning(false); setProgress(0);
    }
  }, [frames, dispatch]);

  if (!frames.length) return <div style={{ padding: 60, textAlign: 'center', color: C.t4 }}><div style={{ fontSize: 36, opacity: .2 }}>??</div>Load a video first.</div>;

  // Stats from faceData
  const framesWithFace   = faceData?.filter(f => f.faces.length > 0).length || 0;
  const totalDetections  = faceData?.reduce((a, f) => a + f.faces.length, 0) || 0;
  const presencePct      = faceData?.length ? +(framesWithFace / faceData.length * 100).toFixed(1) : 0;
  const multiShotFrames  = faceData?.filter(f => f.faces.length > 1).length || 0;

  // Screen-time chart (face count over time)
  const screenTimeline = faceData?.map(f => ({ t: f.time, faces: f.faces.length, maxConf: Math.max(0, ...f.faces.map(fc => fc.probability)) })) || [];

  // 3x3 presence heatmap
  const heatGrid = Array(9).fill(0);
  faceData?.forEach(f => f.faces.forEach(face => {
    const cx = (face.topLeft[0] + face.bottomRight[0]) / 2;
    const cy = (face.topLeft[1] + face.bottomRight[1]) / 2;
    const col = Math.min(2, Math.floor(cx * 3));
    const row = Math.min(2, Math.floor(cy * 3));
    heatGrid[row * 3 + col]++;
  }));
  const heatMax = Math.max(...heatGrid, 1);

  const frame = frames[selIdx];
  const frameResult = faceData?.[Math.round(selIdx / frames.length * (faceData.length - 1))];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Card>
        <Head label="BLAZEFACE � IN-BROWSER FACE DETECTION" accent={C.pink} sub="TensorFlow.js BlazeFace � runs entirely on-device � no data sent anywhere" />
        <Body>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Btn onClick={runDetection} color={C.pink} disabled={running}>{running ? `? Detecting� ${progress}%` : '? DETECT FACES'}</Btn>
            {running && <div style={{ flex: 1, maxWidth: 200 }}><PBar pct={progress} color={C.pink} h={5} /></div>}
            {faceData && <span style={{ fontSize: 10, color: C.green }}>? {faceData.length} frames analysed � {totalDetections} total detections</span>}
          </div>
          {error && <div style={{ marginTop: 10, fontSize: 10, color: C.orange, padding: '8px 12px', background: `${C.orange}10`, border: `1px solid ${C.orange}33`, borderRadius: 3 }}>? {error}</div>}
        </Body>
      </Card>

      {faceData && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
            <MB label="FACE PRESENCE"  value={`${presencePct}%`}     color={C.pink}   sub="of sampled frames" />
            <MB label="TOTAL DETECTS"  value={totalDetections}         color={C.violet} />
            <MB label="MULTI-FACE"     value={multiShotFrames}         color={C.gold}   sub="frames with 2+ faces" />
            <MB label="PEAK FACES"     value={Math.max(...faceData.map(f => f.faces.length), 0)} color={C.cyan} sub="in one frame" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            {/* Face presence heatmap */}
            <Card>
              <Head label="FACE POSITION HEATMAP" accent={C.pink} sub="Where faces appear on screen across all frames" />
              <Body>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 3, background: C.deep, padding: 8, borderRadius: 4 }}>
                  {heatGrid.map((v, i) => {
                    const heat = v / heatMax;
                    return (
                      <div key={i} style={{ height: 80, borderRadius: 3, background: `rgba(236,72,153,${0.05 + heat * 0.85})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: v > 0 ? 18 : 11, color: `rgba(255,255,255,${0.3 + heat * 0.7})`, fontFamily: 'Syne,sans-serif', fontWeight: 800 }}>
                        {v > 0 ? v : '�'}
                      </div>
                    );
                  })}
                </div>
                <div style={{ fontSize: 9, color: C.t3, marginTop: 8, lineHeight: 1.5 }}>Each cell = screen region. Number = total face detections in that zone. Brighter = more faces.</div>
              </Body>
            </Card>

            {/* Frame inspector with bounding boxes */}
            <Card>
              <Head label="FRAME INSPECTOR" accent={C.pink} sub="Select frame below to see detections" />
              <Body style={{ padding: 10 }}>
                {frame && (
                  <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                    <img src={frame.url} alt="" style={{ width: '100%', borderRadius: 3, display: 'block' }} />
                    {/* Draw bounding boxes as overlays */}
                    {frameResult?.faces.map((face, i) => {
                      const imgW = 100, imgH = 100; // percent-based
                      const left  = face.topLeft[0]     * 100;
                      const top   = face.topLeft[1]     * 100;
                      const width = (face.bottomRight[0] - face.topLeft[0]) * 100;
                      const height= (face.bottomRight[1] - face.topLeft[1]) * 100;
                      return (
                        <div key={i} style={{ position: 'absolute', left: `${left}%`, top: `${top}%`, width: `${width}%`, height: `${height}%`, border: `2px solid ${C.pink}`, borderRadius: 2, boxSizing: 'border-box' }}>
                          <div style={{ position: 'absolute', top: -14, left: 0, fontSize: 7, background: C.pink, color: '#fff', padding: '1px 4px', borderRadius: 2, whiteSpace: 'nowrap', fontFamily: 'JetBrains Mono,monospace' }}>
                            {(face.probability * 100).toFixed(0)}%
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div style={{ marginTop: 8 }}>
                  <FilmStrip frames={frames.filter((_, i) => i % Math.max(1, Math.floor(frames.length / 16)) === 0)} selected={selIdx} onSelect={setSelIdx} height={40} />
                </div>
              </Body>
            </Card>
          </div>

          {/* Face presence timeline */}
          <Card>
            <Head label="FACE PRESENCE TIMELINE" accent={C.pink} sub="Number of faces detected per frame over time" />
            <Body style={{ padding: 10 }}>
              <ResponsiveContainer width="100%" height={160}>
                <AreaChart data={screenTimeline} margin={{ top: 4, right: 10, bottom: 10, left: 0 }}>
                  <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
                  <XAxis dataKey="t" tick={{ fill: C.t4, fontSize: 8 }} tickFormatter={fmtTime} />
                  <YAxis tick={{ fill: C.t4, fontSize: 8 }} allowDecimals={false} />
                  <Tooltip content={<ChartTip />} labelFormatter={fmtTime} />
                  <Area type="stepAfter" dataKey="faces" stroke={C.pink} fill={C.pink} fillOpacity={0.3} strokeWidth={2} dot={false} isAnimationActive={false} name="Faces" />
                </AreaChart>
              </ResponsiveContainer>
            </Body>
          </Card>
        </>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// -- 09  OBJECTS TAB � COCO-SSD tracking --------------------------------------
// -----------------------------------------------------------------------------

function ObjectsTab({ state, dispatch }) {
  const { frames, meta, objectData } = state;
  const [running, setRunning]   = useState(false);
  const [progress, setProgress] = useState(0);
  const [selClass, setSelClass] = useState(null);
  const [error, setError]       = useState(null);
  const [selIdx, setSelIdx]     = useState(0);

  const runDetection = useCallback(async () => {
    if (!frames.length) return;
    setRunning(true); setError(null);
    try {
      setProgress(5);
      const coco = await loadCocoSsd();
      const model = await coco.load();
      setProgress(20);

      const results = [];
      const sampleFrames = frames.filter((_, i) => i % Math.max(1, Math.floor(frames.length / 80)) === 0);

      for (let i = 0; i < sampleFrames.length; i++) {
        const f = sampleFrames[i];
        const img = new Image(); img.src = f.url;
        await new Promise(r => { img.onload = r; if (img.complete) r(); });
        try {
          const preds = await model.detect(img);
          results.push({
            time: f.time,
            objects: preds.map(p => ({
              class: p.class, score: +(p.score).toFixed(3),
              bbox: [p.bbox[0] / f.W, p.bbox[1] / f.H, p.bbox[2] / f.W, p.bbox[3] / f.H],
            })),
          });
        } catch { results.push({ time: f.time, objects: [] }); }
        setProgress(20 + Math.round(i / sampleFrames.length * 75));
        await new Promise(r => setTimeout(r, 0));
      }

      dispatch({ type: 'OBJECT_DATA', objectData: results });
      setProgress(100);
      setTimeout(() => { setRunning(false); setProgress(0); }, 500);
    } catch (e) { setError(`COCO-SSD: ${e.message}`); setRunning(false); setProgress(0); }
  }, [frames, dispatch]);

  if (!frames.length) return <div style={{ padding: 60, textAlign: 'center', color: C.t4 }}><div style={{ fontSize: 36, opacity: .2 }}>??</div>Load a video first.</div>;

  // Aggregate stats
  const classCounts = {};
  const classTimeline = {};
  objectData?.forEach(f => f.objects.forEach(obj => {
    classCounts[obj.class] = (classCounts[obj.class] || 0) + 1;
    if (!classTimeline[obj.class]) classTimeline[obj.class] = [];
    classTimeline[obj.class].push({ t: f.time, count: 1 });
  }));
  const sortedClasses = Object.entries(classCounts).sort((a, b) => b[1] - a[1]);
  const totalClasses  = sortedClasses.length;
  const totalObjects  = Object.values(classCounts).reduce((a, b) => a + b, 0);

  // Co-occurrence matrix (which classes appear together)
  const coMatrix = {};
  objectData?.forEach(f => {
    const classes = [...new Set(f.objects.map(o => o.class))];
    classes.forEach(a => classes.forEach(b => {
      if (a !== b) { const key = [a, b].sort().join('|'); coMatrix[key] = (coMatrix[key] || 0) + 1; }
    }));
  });
  const topCo = Object.entries(coMatrix).sort((a, b) => b[1] - a[1]).slice(0, 8);

  const frame = frames[selIdx];
  const frameResult = objectData?.[Math.round(selIdx / frames.length * (objectData.length - 1))];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Card>
        <Head label="COCO-SSD � 80-CLASS OBJECT DETECTION" accent={C.green} sub="TensorFlow.js COCO-SSD � 80 categories � bounding boxes � tracking timeline" />
        <Body>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Btn onClick={runDetection} color={C.green} disabled={running}>{running ? `? Detecting� ${progress}%` : '? DETECT OBJECTS'}</Btn>
            {running && <div style={{ flex: 1, maxWidth: 200 }}><PBar pct={progress} color={C.green} h={5} /></div>}
            {objectData && <span style={{ fontSize: 10, color: C.green }}>? {totalClasses} classes � {totalObjects} detections</span>}
          </div>
          {error && <div style={{ marginTop: 10, fontSize: 10, color: C.orange, padding: '8px 12px', background: `${C.orange}10`, border: `1px solid ${C.orange}33`, borderRadius: 3 }}>? {error}</div>}
        </Body>
      </Card>

      {objectData && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
            <MB label="UNIQUE CLASSES"   value={totalClasses}   color={C.green} />
            <MB label="TOTAL DETECTIONS" value={totalObjects}   color={C.cyan}  />
            <MB label="TOP OBJECT"       value={sortedClasses[0]?.[0] || '�'} color={C.gold} />
            <MB label="CO-OCCURRENCES"   value={topCo.length}   color={C.violet} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            {/* Class frequency chart */}
            <Card>
              <Head label="TOP DETECTED CLASSES" accent={C.green} />
              <Body style={{ padding: 10 }}>
                <ResponsiveContainer width="100%" height={Math.min(400, sortedClasses.slice(0, 15).length * 28)}>
                  <BarChart data={sortedClasses.slice(0, 15).map(([cls, cnt]) => ({ cls, cnt }))} layout="vertical" barCategoryGap="15%" margin={{ left: 10, right: 40 }}>
                    <CartesianGrid strokeDasharray="2 2" stroke={C.b0} horizontal={false} />
                    <XAxis type="number" tick={{ fill: C.t4, fontSize: 8 }} />
                    <YAxis dataKey="cls" type="category" tick={{ fill: C.t2, fontSize: 9 }} width={90} />
                    <Tooltip content={<ChartTip />} />
                    <Bar dataKey="cnt" name="Detections" radius={[0, 4, 4, 0]} onClick={e => setSelClass(selClass === e.cls ? null : e.cls)}>
                      {sortedClasses.slice(0, 15).map(([cls], i) => <Cell key={cls} fill={selClass === cls ? C.cyan : PALETTE[i % PALETTE.length]} opacity={selClass && selClass !== cls ? 0.4 : 0.9} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Body>
            </Card>

            {/* Frame inspector */}
            <Card>
              <Head label="FRAME INSPECTOR � objects overlay" accent={C.green} />
              <Body style={{ padding: 10 }}>
                {frame && (
                  <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                    <img src={frame.url} alt="" style={{ width: '100%', borderRadius: 3, display: 'block' }} />
                    {frameResult?.objects.filter(o => !selClass || o.class === selClass).map((obj, i) => {
                      const color = PALETTE[sortedClasses.findIndex(([c]) => c === obj.class) % PALETTE.length];
                      return (
                        <div key={i} style={{ position: 'absolute', left: `${obj.bbox[0] * 100}%`, top: `${obj.bbox[1] * 100}%`, width: `${obj.bbox[2] * 100}%`, height: `${obj.bbox[3] * 100}%`, border: `2px solid ${color}`, borderRadius: 2, boxSizing: 'border-box' }}>
                          <div style={{ position: 'absolute', top: -14, left: 0, fontSize: 7, background: color, color: '#000', padding: '1px 4px', borderRadius: 2, whiteSpace: 'nowrap', fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>
                            {obj.class} {(obj.score * 100).toFixed(0)}%
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div style={{ marginTop: 8 }}>
                  <FilmStrip frames={frames.filter((_, i) => i % Math.max(1, Math.floor(frames.length / 14)) === 0)} selected={selIdx} onSelect={setSelIdx} height={40} />
                </div>
              </Body>
            </Card>
          </div>

          {/* Class timeline */}
          {selClass && classTimeline[selClass] && (
            <Card>
              <Head label={`${selClass.toUpperCase()} � PRESENCE TIMELINE`} accent={C.green} />
              <Body style={{ padding: 10 }}>
                <ResponsiveContainer width="100%" height={120}>
                  <AreaChart data={(() => { const d = {}; classTimeline[selClass].forEach(e => { d[e.t] = (d[e.t] || 0) + 1; }); return Object.entries(d).map(([t, c]) => ({ t: +t, count: c })); })()} margin={{ top: 4, right: 10, bottom: 0, left: 0 }}>
                    <Area type="stepAfter" dataKey="count" stroke={C.green} fill={C.green} fillOpacity={0.3} strokeWidth={2} dot={false} isAnimationActive={false} />
                    <XAxis dataKey="t" tick={{ fill: C.t4, fontSize: 8 }} tickFormatter={fmtTime} />
                    <YAxis tick={{ fill: C.t4, fontSize: 8 }} allowDecimals={false} />
                    <Tooltip content={<ChartTip />} labelFormatter={fmtTime} />
                  </AreaChart>
                </ResponsiveContainer>
              </Body>
            </Card>
          )}

          {/* Co-occurrence table */}
          {topCo.length > 0 && (
            <Card>
              <Head label="OBJECT CO-OCCURRENCE � what appears together" accent={C.violet} sub="How often two classes share the same frame" />
              <Body>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {topCo.map(([pair, cnt], i) => {
                    const [a, b] = pair.split('|');
                    return (
                      <div key={pair} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: C.raised, border: `1px solid ${C.b1}`, borderRadius: 3 }}>
                        <span style={{ fontSize: 9, color: PALETTE[i % PALETTE.length], fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{a}</span>
                        <span style={{ fontSize: 8, color: C.t4 }}>+</span>
                        <span style={{ fontSize: 9, color: PALETTE[(i + 3) % PALETTE.length], fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{b}</span>
                        <span style={{ marginLeft: 'auto', fontSize: 13, fontFamily: 'Syne,sans-serif', fontWeight: 800, color: C.t1 }}>{cnt}</span>
                      </div>
                    );
                  })}
                </div>
              </Body>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// -- 10  COLOR GRADE TAB ------------------------------------------------------
// -----------------------------------------------------------------------------

function ColorGradeTab({ state }) {
  const { timeline, frames, grade, meta } = state;
  const [view, setView] = useState('grade');

  if (!timeline.length) return <div style={{ padding: 60, textAlign: 'center', color: C.t4 }}><div style={{ fontSize: 36, opacity: .2 }}>??</div>Load a video first.</div>;

  const dur = meta?.duration || 1;

  // Global palette: aggregate mean colors per frame, bucket into 16 colors
  const globalPalette = useMemo(() => {
    const buckets = {};
    timeline.forEach(f => {
      const r = Math.round(f.r / 16) * 16, g = Math.round(f.g / 16) * 16, b = Math.round(f.b / 16) * 16;
      const key = `${r},${g},${b}`;
      if (!buckets[key]) buckets[key] = { r, g, b, count: 0 };
      buckets[key].count++;
    });
    return Object.values(buckets).sort((a, b) => b.count - a.count).slice(0, 14).map(c => ({
      ...c,
      hex: '#' + [c.r, c.g, c.b].map(v => v.toString(16).padStart(2, '0')).join(''),
      pct: +(c.count / timeline.length * 100).toFixed(1),
    }));
  }, [timeline]);

  // RGB channel over time
  const avgR = (timeline.reduce((a, f) => a + f.r, 0) / timeline.length).toFixed(0);
  const avgG = (timeline.reduce((a, f) => a + f.g, 0) / timeline.length).toFixed(0);
  const avgB = (timeline.reduce((a, f) => a + f.b, 0) / timeline.length).toFixed(0);
  const avgWarmth = (timeline.reduce((a, f) => a + f.warmth, 0) / timeline.length).toFixed(1);
  const avgSat    = (timeline.reduce((a, f) => a + f.saturation, 0) / timeline.length).toFixed(1);
  const avgContrast = (timeline.reduce((a, f) => a + f.contrast, 0) / timeline.length).toFixed(1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', gap: 4 }}>
        {[['grade','? GRADE STYLE'],['palette','? PALETTE'],['rgb','� RGB CHANNELS'],['waveform','? VECTORSCOPE']].map(([v, l]) => (
          <button key={v} onClick={() => setView(v)} style={{ padding: '6px 14px', border: `1px solid ${view === v ? C.gold : C.b0}`, background: view === v ? `${C.gold}15` : C.raised, color: view === v ? C.gold : C.t2, fontSize: 9, cursor: 'pointer', borderRadius: 3, fontFamily: 'JetBrains Mono,monospace', fontWeight: view === v ? 700 : 400 }}>{l}</button>
        ))}
      </div>

      {/* Grade style card */}
      {grade && (
        <Card style={{ borderTop: `3px solid ${grade.color}` }}>
          <Body>
            <div style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 9, color: grade.color, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, letterSpacing: '.14em', marginBottom: 4 }}>DETECTED GRADE STYLE</div>
                <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 900, fontSize: 28, color: C.t1, lineHeight: 1 }}>{grade.style}</div>
                <div style={{ fontSize: 11, color: C.t2, marginTop: 8, lineHeight: 1.65, maxWidth: 340 }}>{grade.desc}</div>
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {[['AVG R', `${avgR}`, C.red], ['AVG G', `${avgG}`, C.green], ['AVG B', `${avgB}`, C.blue], ['WARMTH', `${avgWarmth}%`, C.orange], ['SAT', `${avgSat}%`, C.violet], ['CONTRAST', `${avgContrast}`, C.gold]].map(([l, v, c]) => (
                  <div key={l} style={{ padding: '8px 12px', background: `${c}12`, border: `1px solid ${c}33`, borderRadius: 3, textAlign: 'center', minWidth: 70 }}>
                    <div style={{ fontSize: 16, fontFamily: 'Syne,sans-serif', fontWeight: 800, color: c }}>{v}</div>
                    <div style={{ fontSize: 8, color: c, fontFamily: 'JetBrains Mono,monospace', marginTop: 3 }}>{l}</div>
                  </div>
                ))}
              </div>
            </div>
          </Body>
        </Card>
      )}

      {view === 'palette' && (
        <>
          {/* Actual color strip */}
          <Card>
            <Head label="FRAME COLOR STRIP � mean pixel color per frame" accent={C.gold} />
            <Body style={{ padding: '10px 10px 6px' }}>
              <div style={{ display: 'flex', height: 64, borderRadius: 3, overflow: 'hidden', gap: '0.5px' }}>
                {timeline.map((f, i) => <div key={i} style={{ flex: 1, background: f.hex, minWidth: 1 }} title={`${fmtTime(f.time)}: ${f.hex}`} />)}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 8, color: C.t4, fontFamily: 'JetBrains Mono,monospace' }}><span>START</span><span>COLOR ARC</span><span>END</span></div>
            </Body>
          </Card>
          {/* Global dominant palette */}
          <Card>
            <Head label={`DOMINANT PALETTE � ${globalPalette.length} colors from ${timeline.length} frames`} accent={C.gold} />
            <Body>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
                {globalPalette.map((c, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, width: 'calc(50% - 6px)' }}>
                    <div style={{ width: 44, height: 44, borderRadius: 4, background: c.hex, flexShrink: 0, boxShadow: '0 2px 10px rgba(0,0,0,.5)' }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 10, color: C.t1, fontWeight: 700 }}>{c.hex.toUpperCase()}</div>
                      <div style={{ fontSize: 8, color: C.t4, marginBottom: 3 }}>RGB({c.r},{c.g},{c.b})</div>
                      <PBar pct={c.pct} color={c.hex} h={4} />
                    </div>
                    <div style={{ fontSize: 14, fontFamily: 'Syne,sans-serif', fontWeight: 800, color: C.t1, minWidth: 36, textAlign: 'right' }}>{c.pct}%</div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', height: 32, borderRadius: 4, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,.4)' }}>
                {globalPalette.map((c, i) => <div key={i} style={{ flex: c.pct, background: c.hex }} title={c.hex} />)}
              </div>
            </Body>
          </Card>
        </>
      )}

      {view === 'rgb' && (
        <>
          <Card>
            <Head label="RGB CHANNEL ARC" accent={C.gold} sub="How red, green, blue balance shifts across the video" />
            <Body style={{ padding: 10 }}>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={timeline} margin={{ top: 4, right: 10, bottom: 16, left: 0 }}>
                  <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
                  <XAxis dataKey="time" tick={{ fill: C.t4, fontSize: 8 }} tickFormatter={fmtTime} />
                  <YAxis tick={{ fill: C.t4, fontSize: 8 }} domain={[0, 255]} />
                  <Tooltip content={<ChartTip />} labelFormatter={fmtTime} />
                  <ReferenceLine y={128} stroke={C.t4} strokeDasharray="3 3" />
                  <Line type="monotone" dataKey="r" stroke={C.red}   dot={false} strokeWidth={1.5} isAnimationActive={false} name="Red" />
                  <Line type="monotone" dataKey="g" stroke={C.green} dot={false} strokeWidth={1.5} isAnimationActive={false} name="Green" />
                  <Line type="monotone" dataKey="b" stroke={C.blue}  dot={false} strokeWidth={1.5} isAnimationActive={false} name="Blue" />
                  <Legend wrapperStyle={{ fontSize: 9 }} />
                </LineChart>
              </ResponsiveContainer>
            </Body>
          </Card>
          {/* Warmth arc */}
          <Card>
            <Head label="WARMTH vs SATURATION ARC" accent={C.orange} />
            <Body style={{ padding: 10 }}>
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={timeline} margin={{ top: 4, right: 10, bottom: 16, left: 0 }}>
                  <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
                  <XAxis dataKey="time" tick={{ fill: C.t4, fontSize: 8 }} tickFormatter={fmtTime} />
                  <YAxis tick={{ fill: C.t4, fontSize: 8 }} domain={[0, 100]} />
                  <Tooltip content={<ChartTip />} labelFormatter={fmtTime} />
                  <Line type="monotone" dataKey="warmth"     stroke={C.orange} dot={false} strokeWidth={1.5} isAnimationActive={false} name="Warmth %" />
                  <Line type="monotone" dataKey="saturation" stroke={C.violet} dot={false} strokeWidth={1.5} isAnimationActive={false} name="Saturation %" />
                  <Legend wrapperStyle={{ fontSize: 9 }} />
                </LineChart>
              </ResponsiveContainer>
            </Body>
          </Card>
        </>
      )}

      {view === 'waveform' && (
        <Card>
          <Head label="COLOUR VECTORSCOPE � R vs B scatter" accent={C.gold} sub="Simulated vectorscope: each point = one frame, x = red mean, y = blue mean" />
          <Body style={{ padding: 10 }}>
            <ResponsiveContainer width="100%" height={300}>
              <ScatterChart margin={{ top: 10, right: 20, bottom: 20, left: 0 }}>
                <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
                <XAxis dataKey="r" name="Red" type="number" domain={[0, 255]} tick={{ fill: C.t4, fontSize: 8 }} label={{ value: 'Red channel', position: 'insideBottom', offset: -10, fill: C.t4, fontSize: 9 }} />
                <YAxis dataKey="b" name="Blue" type="number" domain={[0, 255]} tick={{ fill: C.t4, fontSize: 8 }} label={{ value: 'Blue', angle: -90, position: 'insideLeft', fill: C.t4, fontSize: 9 }} />
                <Tooltip content={({ active, payload }) => active && payload?.[0] ? (
                  <div style={{ background: C.surface, border: `1px solid ${C.b2}`, padding: '6px 10px', fontSize: 9, borderRadius: 3 }}>
                    <div>R:{payload[0].payload.r} G:{payload[0].payload.g} B:{payload[0].payload.b}</div>
                    <div style={{ color: C.t4 }}>{fmtTime(payload[0].payload.time)}</div>
                  </div>
                ) : null} />
                <ReferenceLine x={128} stroke={C.t4} strokeDasharray="3 3" />
                <ReferenceLine y={128} stroke={C.t4} strokeDasharray="3 3" />
                <Scatter data={timeline} fill={C.gold} opacity={0.5} r={2} />
              </ScatterChart>
            </ResponsiveContainer>
            <div style={{ fontSize: 9, color: C.t3, fontFamily: 'JetBrains Mono,monospace', marginTop: 4, display: 'flex', gap: 14 }}>
              <span>Top-left = cool blue � Bottom-right = warm orange � Center = neutral</span>
            </div>
          </Body>
        </Card>
      )}

      {view === 'grade' && grade && (
        <>
          {/* Radar summary */}
          <Card>
            <Head label="VISUAL SIGNATURE RADAR" accent={grade.color} sub="Multi-dimensional cinematic fingerprint" />
            <Body style={{ padding: 10 }}>
              <ResponsiveContainer width="100%" height={260}>
                <RadarChart data={[
                  { axis: 'Brightness',  value: Math.round(+avgR / 255 * 100) },
                  { axis: 'Saturation',  value: Math.round(+avgSat) },
                  { axis: 'Contrast',    value: Math.min(100, Math.round(+avgContrast * 1.5)) },
                  { axis: 'Warmth',      value: Math.round(+avgWarmth) },
                  { axis: 'Green Shift', value: Math.round(+avgG / 255 * 100) },
                  { axis: 'Blue Depth',  value: Math.round(+avgB / 255 * 100) },
                ]} cx="50%" cy="50%" outerRadius="70%">
                  <PolarGrid stroke={C.b1} />
                  <PolarAngleAxis dataKey="axis" tick={{ fill: C.t3, fontSize: 9 }} />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: C.t4, fontSize: 7 }} />
                  <Radar name="Video" dataKey="value" stroke={grade.color} fill={grade.color} fillOpacity={0.3} />
                </RadarChart>
              </ResponsiveContainer>
            </Body>
          </Card>
          {/* Cinematic LUTs comparison */}
          <Card>
            <Head label="CLOSEST CINEMATIC LUT FINGERPRINTS" accent={grade.color} />
            <Body>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  ['Orange-Teal',      Math.max(0, 100 - Math.abs(+avgWarmth - 65) * 2),              C.orange],
                  ['Bleach Bypass',    Math.max(0, 100 - +avgSat * 2),                                 C.t3],
                  ['Airy / Faded',     Math.max(0, 100 - Math.abs(+avgContrast - 15) * 3),             C.cyan],
                  ['Dark Cinematic',   Math.max(0, 100 - Math.abs(+avgR / 255 * 100 - 30) * 2),        C.violet],
                  ['Vibrant / Pop',    Math.max(0, Math.min(100, +avgSat * 1.5 - 20)),                 C.gold],
                  ['Natural / Flat',   Math.max(0, 100 - Math.abs(50 - +avgWarmth) - Math.abs(50 - +avgSat)), C.green],
                ].sort((a, b) => b[1] - a[1]).map(([name, score, color]) => (
                  <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ fontSize: 10, color: C.t2, minWidth: 130 }}>{name}</div>
                    <div style={{ flex: 1 }}><PBar pct={score} color={color} h={6} /></div>
                    <div style={{ fontSize: 11, color, fontFamily: 'Syne,sans-serif', fontWeight: 700, minWidth: 36, textAlign: 'right' }}>{Math.round(score)}%</div>
                  </div>
                ))}
              </div>
            </Body>
          </Card>
        </>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// -- 11  AUDIO SYNC TAB -------------------------------------------------------
// -----------------------------------------------------------------------------

function AudioSyncTab({ state }) {
  const { audio, timeline, cuts, meta } = state;

  if (!timeline.length) return <div style={{ padding: 60, textAlign: 'center', color: C.t4 }}><div style={{ fontSize: 36, opacity: .2 }}>??</div>Load a video first.</div>;

  const dur = meta?.duration || 1;
  const hasAudio = !!audio;

  if (!hasAudio) return <Info color={C.violet}>Audio could not be extracted from this video (codec not supported by Web Audio API). Try converting to MP4/H.264 first.</Info>;

  const { timeline: atl, silenceRatio } = audio;

  // Align audio timeline with video timeline
  const avgAudioEnergy = atl.reduce((a, f) => a + f.rms, 0) / atl.length;
  const maxRMS = Math.max(...atl.map(f => f.rms), 0.001);

  // Find audio peaks (potential beat points)
  const peaks = [];
  for (let i = 2; i < atl.length - 2; i++) {
    if (atl[i].rms > atl[i-1].rms && atl[i].rms > atl[i-2].rms && atl[i].rms > atl[i+1].rms && atl[i].rms > atl[i+2].rms && atl[i].rms > avgAudioEnergy * 2.5) {
      peaks.push({ t: atl[i].t, rms: atl[i].rms });
    }
  }

  // Beats per minute estimation
  const beatIntervals = peaks.slice(1).map((p, i) => p.t - peaks[i].t).filter(iv => iv > 0.2 && iv < 3);
  const avgInterval   = beatIntervals.length ? beatIntervals.reduce((a, b) => a + b, 0) / beatIntervals.length : 0;
  const estimatedBPM  = avgInterval > 0 ? Math.round(60 / avgInterval) : 0;

  // Cuts that align with audio peaks (within 0.25s)
  const syncedCuts = cuts?.filter(c => peaks.some(p => Math.abs(p.t - c.t) < 0.25)).length || 0;
  const cutSyncPct  = cuts?.length ? +(syncedCuts / cuts.length * 100).toFixed(1) : 0;

  // Combined sync chart: normalize both to 0-100
  const syncData = atl.map(af => {
    const vf = timeline[Math.round(af.t / dur * (timeline.length - 1))] || {};
    return {
      t: af.t,
      audio: +(af.rms / maxRMS * 100).toFixed(1),
      motion: +(vf.motionScore / Math.max(...timeline.map(f => f.motionScore), 1) * 100).toFixed(1),
      brightness: vf.brightness || 0,
    };
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 10 }}>
        <MB label="SILENCE RATIO"  value={`${silenceRatio}%`}   color={C.violet}                                        sub="% quiet" />
        <MB label="EST. BPM"       value={estimatedBPM || '�'}  color={C.green}                                         sub="from energy peaks" />
        <MB label="AUDIO PEAKS"    value={peaks.length}          color={C.cyan}                                          />
        <MB label="CUT-BEAT SYNC"  value={`${cutSyncPct}%`}     color={cutSyncPct > 60 ? C.green : C.gold}              sub={`${syncedCuts}/${cuts?.length || 0} cuts on beat`} />
        <MB label="SPEECH DENSITY" value={`${(100 - silenceRatio).toFixed(1)}%`} color={C.orange}                       sub="non-silent frames" />
      </div>

      {/* Master sync chart */}
      <Card>
        <Head label="AUDIO + MOTION SYNC � aligned timeline" accent={C.violet} sub="Orange = motion energy � Purple = audio RMS � peaks = high-energy moments" />
        <Body style={{ padding: 10 }}>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={syncData} margin={{ top: 4, right: 10, bottom: 16, left: 0 }}>
              <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
              <XAxis dataKey="t" tick={{ fill: C.t4, fontSize: 8 }} tickFormatter={fmtTime} />
              <YAxis tick={{ fill: C.t4, fontSize: 8 }} domain={[0, 100]} />
              <Tooltip content={<ChartTip />} labelFormatter={fmtTime} />
              {cuts?.map((c, i) => <ReferenceLine key={i} x={c.t} stroke={C.red} strokeOpacity={0.5} strokeWidth={1} />)}
              {peaks.slice(0, 40).map((p, i) => <ReferenceLine key={i} x={p.t} stroke={C.green} strokeOpacity={0.35} strokeDasharray="2 2" strokeWidth={1} />)}
              <Line type="monotone" dataKey="audio"  stroke={C.violet} dot={false} strokeWidth={1.5} isAnimationActive={false} name="Audio" />
              <Line type="monotone" dataKey="motion" stroke={C.orange} dot={false} strokeWidth={1.5} isAnimationActive={false} name="Motion" />
              <Legend wrapperStyle={{ fontSize: 9 }} />
            </LineChart>
          </ResponsiveContainer>
          <div style={{ fontSize: 8, color: C.t4, fontFamily: 'JetBrains Mono,monospace', display: 'flex', gap: 14, marginTop: 4 }}>
            <span style={{ color: C.red }}>?RED = video cuts</span>
            <span style={{ color: C.green }}>?GREEN = audio peaks</span>
          </div>
        </Body>
      </Card>

      {/* Audio waveform */}
      <Card>
        <Head label="AUDIO WAVEFORM" accent={C.violet} sub={`${audio.duration.toFixed(1)}s � ${audio.sampleRate / 1000}kHz � silence: ${silenceRatio}%`} />
        <Body style={{ padding: 10 }}>
          <ResponsiveContainer width="100%" height={100}>
            <AreaChart data={atl} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
              <defs><linearGradient id="aGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.violet} stopOpacity={0.6} /><stop offset="100%" stopColor={C.violet} stopOpacity={0.05} /></linearGradient></defs>
              <Area type="monotone" dataKey="rms" stroke={C.violet} fill="url(#aGrad)" strokeWidth={1} dot={false} isAnimationActive={false} />
              <XAxis dataKey="t" hide />
              <YAxis hide />
            </AreaChart>
          </ResponsiveContainer>
        </Body>
      </Card>

      {/* Peak beat moments */}
      {peaks.length > 0 && (
        <Card>
          <Head label="ENERGY PEAKS � potential beat/impact moments" accent={C.green} sub="Sudden amplitude spikes � music hits, sound effects, dialogue bursts" />
          <Body style={{ maxHeight: 300, overflowY: 'auto', padding: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: C.deep }}>
                {['#', 'TIME', 'AMPLITUDE', 'INTENSITY', 'CUT NEARBY?'].map(h => <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontSize: 8, color: C.t3, fontFamily: 'JetBrains Mono,monospace', borderBottom: `1px solid ${C.b1}` }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {peaks.map((p, i) => {
                  const intensity = p.rms / maxRMS;
                  const nearCut = cuts?.some(c => Math.abs(c.t - p.t) < 0.25);
                  return (
                    <tr key={i} style={{ borderBottom: `1px solid ${C.b0}`, background: i % 2 === 0 ? 'transparent' : `${C.raised}60` }}>
                      <td style={{ padding: '5px 10px', fontSize: 9, color: C.t4, fontFamily: 'JetBrains Mono,monospace' }}>{i + 1}</td>
                      <td style={{ padding: '5px 10px', fontSize: 9, color: C.violet, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{fmtTime(p.t)}</td>
                      <td style={{ padding: '5px 10px', fontSize: 9, color: C.t1, fontFamily: 'JetBrains Mono,monospace' }}>{(p.rms * 1000).toFixed(1)}�</td>
                      <td style={{ padding: '5px 10px' }}><PBar pct={intensity * 100} color={intensity > 0.7 ? C.red : intensity > 0.4 ? C.orange : C.gold} h={5} /></td>
                      <td style={{ padding: '5px 10px', fontSize: 9, color: nearCut ? C.green : C.t4, fontFamily: 'JetBrains Mono,monospace', fontWeight: nearCut ? 700 : 400 }}>{nearCut ? '? YES' : '�'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Body>
        </Card>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// -- 12  QUALITY TAB ----------------------------------------------------------
// -----------------------------------------------------------------------------

function QualityTab({ state }) {
  const { timeline, frames, meta } = state;
  const [selIdx, setSelIdx] = useState(0);

  if (!timeline.length) return <div style={{ padding: 60, textAlign: 'center', color: C.t4 }}><div style={{ fontSize: 36, opacity: .2 }}>??</div>Load a video first.</div>;

  const maxSharp = Math.max(...timeline.map(f => f.sharpness), 1);
  const maxNoise = Math.max(...timeline.map(f => f.noiseLevel || 0), 0.01);

  const avgSharp   = timeline.reduce((a, f) => a + f.sharpness, 0) / timeline.length;
  const avgNoise   = timeline.reduce((a, f) => a + (f.noiseLevel || 0), 0) / timeline.length;
  const avgClipH   = timeline.reduce((a, f) => a + f.clippingHigh, 0) / timeline.length;
  const avgClipL   = timeline.reduce((a, f) => a + f.clippingLow, 0) / timeline.length;

  // Stability: variance of brightness between frames (low = stable shot, high = handheld/cut)
  const brightVals  = timeline.map(f => f.brightness);
  const brightMean  = brightVals.reduce((a, b) => a + b, 0) / brightVals.length;
  const stabilityScore = Math.max(0, 100 - Math.sqrt(brightVals.reduce((a, v) => a + (v - brightMean) ** 2, 0) / brightVals.length) * 0.8);

  // Focus histogram
  const sharpBins = Array.from({ length: 10 }, (_, i) => ({ range: `${i * 10}-${(i + 1) * 10}%`, count: 0 }));
  timeline.forEach(f => {
    const pct = f.sharpness / maxSharp * 100;
    const bi  = Math.min(9, Math.floor(pct / 10));
    sharpBins[bi].count++;
  });

  // Best / worst frames
  const sortedBySharpness = [...timeline].sort((a, b) => b.sharpness - a.sharpness);
  const sharpestFrameIdx  = timeline.indexOf(sortedBySharpness[0]);
  const blurriestFrameIdx = timeline.indexOf(sortedBySharpness[sortedBySharpness.length - 1]);

  const qualityScore = Math.round(
    (avgSharp / maxSharp * 40) +
    (Math.max(0, 30 - avgNoise * 500) * 0.5) +
    (Math.max(0, 15 - avgClipH) * 0.8) +
    stabilityScore * 0.15
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 10 }}>
        <MB label="QUALITY SCORE"   value={`${qualityScore}/100`}             color={qualityScore > 70 ? C.green : qualityScore > 45 ? C.gold : C.red}  sub="composite" />
        <MB label="AVG SHARPNESS"   value={avgSharp.toFixed(0)}               color={C.cyan}    sub="Laplacian variance" />
        <MB label="AVG NOISE"       value={`${(avgNoise * 1000).toFixed(1)}�`} color={avgNoise > 0.02 ? C.red : C.green} />
        <MB label="OVEREXP. CLIP"   value={`${avgClipH.toFixed(1)}%`}         color={avgClipH > 5 ? C.red : C.gold}  sub="% blown highlights" />
        <MB label="STABILITY"       value={`${stabilityScore.toFixed(0)}/100`} color={stabilityScore > 70 ? C.green : C.gold} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        {/* Sharpness over time */}
        <Card>
          <Head label="FOCUS/SHARPNESS OVER TIME" accent={C.cyan} sub="Laplacian variance per frame � drops = blur / rack focus" />
          <Body style={{ padding: 10 }}>
            <ResponsiveContainer width="100%" height={160}>
              <AreaChart data={timeline.map(f => ({ ...f, sharpPct: +(f.sharpness / maxSharp * 100).toFixed(1) }))} margin={{ top: 4, right: 6, bottom: 10, left: 0 }}>
                <defs><linearGradient id="shGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.cyan} stopOpacity={0.45} /><stop offset="100%" stopColor={C.cyan} stopOpacity={0.02} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
                <XAxis dataKey="time" tick={{ fill: C.t4, fontSize: 8 }} tickFormatter={fmtTime} />
                <YAxis tick={{ fill: C.t4, fontSize: 8 }} domain={[0, 100]} />
                <Tooltip content={<ChartTip />} labelFormatter={fmtTime} />
                <Area type="monotone" dataKey="sharpPct" stroke={C.cyan} fill="url(#shGrad)" strokeWidth={1.5} dot={false} isAnimationActive={false} name="Sharpness %" />
              </AreaChart>
            </ResponsiveContainer>
          </Body>
        </Card>

        {/* Noise over time */}
        <Card>
          <Head label="NOISE LEVEL OVER TIME" accent={C.red} sub="High-frequency variation � grain, sensor noise, compression artefacts" />
          <Body style={{ padding: 10 }}>
            <ResponsiveContainer width="100%" height={160}>
              <AreaChart data={timeline.map(f => ({ ...f, noisePct: Math.min(100, +((f.noiseLevel || 0) / maxNoise * 100).toFixed(1)) }))} margin={{ top: 4, right: 6, bottom: 10, left: 0 }}>
                <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
                <XAxis dataKey="time" tick={{ fill: C.t4, fontSize: 8 }} tickFormatter={fmtTime} />
                <YAxis tick={{ fill: C.t4, fontSize: 8 }} domain={[0, 100]} />
                <Tooltip content={<ChartTip />} labelFormatter={fmtTime} />
                <Area type="monotone" dataKey="noisePct" stroke={C.red} fill={C.red} fillOpacity={0.25} strokeWidth={1.5} dot={false} isAnimationActive={false} name="Noise %" />
              </AreaChart>
            </ResponsiveContainer>
          </Body>
        </Card>
      </div>

      {/* Clipping chart */}
      <Card>
        <Head label="EXPOSURE CLIPPING � highlight + shadow crush" accent={C.gold} sub="% of pixels blown out (>235 luma) or crushed (<20 luma)" />
        <Body style={{ padding: 10 }}>
          <ResponsiveContainer width="100%" height={140}>
            <LineChart data={timeline} margin={{ top: 4, right: 10, bottom: 10, left: 0 }}>
              <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
              <XAxis dataKey="time" tick={{ fill: C.t4, fontSize: 8 }} tickFormatter={fmtTime} />
              <YAxis tick={{ fill: C.t4, fontSize: 8 }} />
              <Tooltip content={<ChartTip />} labelFormatter={fmtTime} />
              <ReferenceLine y={5} stroke={C.orange} strokeDasharray="3 3" />
              <Line type="monotone" dataKey="clippingHigh" stroke={C.gold}  dot={false} strokeWidth={1.5} isAnimationActive={false} name="Highlights %" />
              <Line type="monotone" dataKey="clippingLow"  stroke={C.blue}  dot={false} strokeWidth={1.5} isAnimationActive={false} name="Shadows %" />
              <Legend wrapperStyle={{ fontSize: 9 }} />
            </LineChart>
          </ResponsiveContainer>
        </Body>
      </Card>

      {/* Sharpness histogram */}
      <Card>
        <Head label="SHARPNESS DISTRIBUTION � how many frames at each focus level" accent={C.cyan} />
        <Body style={{ padding: 10 }}>
          <ResponsiveContainer width="100%" height={140}>
            <BarChart data={sharpBins} barCategoryGap="8%">
              <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
              <XAxis dataKey="range" tick={{ fill: C.t4, fontSize: 8 }} />
              <YAxis tick={{ fill: C.t4, fontSize: 8 }} />
              <Tooltip content={<ChartTip />} />
              <Bar dataKey="count" name="Frames" radius={[3, 3, 0, 0]}>
                {sharpBins.map((_, i) => <Cell key={i} fill={i > 6 ? C.cyan : i > 3 ? C.gold : C.red} opacity={0.85} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Body>
      </Card>

      {/* Best / worst frames */}
      {frames.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          {[['SHARPEST FRAME', sharpestFrameIdx, C.green], ['BLURRIEST FRAME', blurriestFrameIdx, C.red]].map(([label, idx, color]) => {
            const f = frames[Math.min(frames.length - 1, idx)];
            const tl = timeline[Math.min(timeline.length - 1, idx)];
            return f ? (
              <Card key={label}>
                <Head label={label} accent={color} sub={`t=${fmtTime(f.time)} � sharpness=${tl?.sharpness?.toFixed(0)}`} />
                <Body style={{ padding: 10 }}>
                  <img src={f.url} alt="" style={{ width: '100%', borderRadius: 3 }} />
                </Body>
              </Card>
            ) : null;
          })}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// -- 13  ENGAGEMENT TAB -------------------------------------------------------
// -----------------------------------------------------------------------------

function EngagementTab({ state }) {
  const { timeline, cuts, frames, meta, audio } = state;

  if (!timeline.length) return <div style={{ padding: 60, textAlign: 'center', color: C.t4 }}><div style={{ fontSize: 36, opacity: .2 }}>??</div>Load a video first.</div>;

  const dur = meta?.duration || 1;

  // Rolling average engagement (smooth curve)
  const WINDOW = 8;
  const smoothed = timeline.map((f, i) => {
    const slice = timeline.slice(Math.max(0, i - WINDOW), i + WINDOW + 1);
    const avg = slice.reduce((a, s) => a + s.engagement, 0) / slice.length;
    return { ...f, smoothEngagement: +avg.toFixed(1) };
  });

  // Peak engagement moments (top 10%)
  const sorted = [...timeline].sort((a, b) => b.engagement - a.engagement);
  const threshold = sorted[Math.floor(sorted.length * 0.1)]?.engagement || 80;
  const peakMoments = timeline.filter(f => f.engagement >= threshold).slice(0, 8);

  // Drop moments
  const dropThreshold = sorted[Math.floor(sorted.length * 0.9)]?.engagement || 20;
  const dropMoments   = timeline.filter(f => f.engagement <= dropThreshold).slice(0, 5);

  const avgEngage = (timeline.reduce((a, f) => a + f.engagement, 0) / timeline.length).toFixed(1);
  const maxEngage = Math.max(...timeline.map(f => f.engagement)).toFixed(1);
  const minEngage = Math.min(...timeline.map(f => f.engagement)).toFixed(1);

  // Engagement distribution
  const engBins = Array.from({ length: 10 }, (_, i) => ({ range: `${i * 10}-${(i + 1) * 10}`, count: 0 }));
  timeline.forEach(f => { engBins[Math.min(9, Math.floor(f.engagement / 10))].count++; });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
        <MB label="AVG ENGAGEMENT" value={`${avgEngage}/100`} color={+avgEngage > 60 ? C.green : +avgEngage > 40 ? C.gold : C.red} />
        <MB label="PEAK"           value={`${maxEngage}/100`} color={C.green} sub={`t=${fmtTime(timeline.find(f => f.engagement === +maxEngage)?.time)}`} />
        <MB label="TROUGH"         value={`${minEngage}/100`} color={C.red}   sub={`t=${fmtTime(timeline.find(f => f.engagement === +minEngage)?.time)}`} />
        <MB label="HIGH-ENGAGE %"  value={`${(timeline.filter(f => f.engagement > 70).length / timeline.length * 100).toFixed(0)}%`} color={C.cyan} sub="frames >70 score" />
      </div>

      {/* Main engagement curve */}
      <Card>
        <Head label="PREDICTED ENGAGEMENT CURVE" accent={C.green} sub="Composite visual dynamics � smoothed � orange = cut points � grey zone = danger area" />
        <Body style={{ padding: 10 }}>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={smoothed} margin={{ top: 4, right: 10, bottom: 16, left: 0 }}>
              <defs>
                <linearGradient id="engGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor={C.green}  stopOpacity={0.5} />
                  <stop offset="60%"  stopColor={C.gold}   stopOpacity={0.3} />
                  <stop offset="100%" stopColor={C.red}    stopOpacity={0.1} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
              <XAxis dataKey="time" tick={{ fill: C.t4, fontSize: 8 }} tickFormatter={fmtTime} label={{ value: 'Time', position: 'insideBottom', offset: -10, fill: C.t4, fontSize: 9 }} />
              <YAxis tick={{ fill: C.t4, fontSize: 8 }} domain={[0, 100]} label={{ value: 'Score', angle: -90, position: 'insideLeft', fill: C.t4, fontSize: 9 }} />
              <Tooltip content={<ChartTip />} labelFormatter={fmtTime} />
              {/* Danger zone */}
              <ReferenceLine y={40} stroke={C.red}   strokeDasharray="4 3" strokeWidth={1.5} label={{ value: 'LOW 40', fill: C.red,   fontSize: 8, position: 'insideRight' }} />
              <ReferenceLine y={70} stroke={C.green} strokeDasharray="4 3" strokeWidth={1.5} label={{ value: 'HIGH 70', fill: C.green, fontSize: 8, position: 'insideRight' }} />
              {/* Cut markers */}
              {cuts?.map((c, i) => <ReferenceLine key={i} x={c.t} stroke={C.orange} strokeOpacity={0.6} strokeWidth={1} />)}
              <Area type="monotone" dataKey="smoothEngagement" stroke={C.green} fill="url(#engGrad)" strokeWidth={2.5} dot={false} isAnimationActive={false} name="Engagement" />
            </AreaChart>
          </ResponsiveContainer>
        </Body>
      </Card>

      {/* Distribution */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Card>
          <Head label="ENGAGEMENT DISTRIBUTION" accent={C.green} />
          <Body style={{ padding: 10 }}>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={engBins} barCategoryGap="8%">
                <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
                <XAxis dataKey="range" tick={{ fill: C.t4, fontSize: 7 }} />
                <YAxis tick={{ fill: C.t4, fontSize: 8 }} />
                <Tooltip content={<ChartTip />} />
                <Bar dataKey="count" name="Frames" radius={[3, 3, 0, 0]}>
                  {engBins.map((_, i) => <Cell key={i} fill={i > 6 ? C.green : i > 3 ? C.gold : C.red} opacity={0.85} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Body>
        </Card>

        {/* Radar: engagement drivers */}
        <Card>
          <Head label="ENGAGEMENT DRIVERS" accent={C.green} />
          <Body style={{ padding: 10 }}>
            <ResponsiveContainer width="100%" height={180}>
              <RadarChart data={[
                { axis: 'Brightness', value: Math.round(timeline.reduce((a, f) => a + (100 - Math.abs(f.brightness - 128) / 1.28), 0) / timeline.length) },
                { axis: 'Contrast',   value: Math.min(100, Math.round(timeline.reduce((a, f) => a + f.contrast, 0) / timeline.length * 1.5)) },
                { axis: 'Saturation', value: Math.round(timeline.reduce((a, f) => a + f.saturation, 0) / timeline.length) },
                { axis: 'Motion',     value: Math.round(timeline.reduce((a, f) => a + Math.min(30, f.motionScore * 2), 0) / timeline.length / 0.3) },
                { axis: 'Sharpness',  value: Math.round(timeline.reduce((a, f) => a + f.sharpness, 0) / timeline.length / (Math.max(...timeline.map(f => f.sharpness), 1)) * 100) },
              ]} cx="50%" cy="50%" outerRadius="65%">
                <PolarGrid stroke={C.b1} />
                <PolarAngleAxis dataKey="axis" tick={{ fill: C.t3, fontSize: 8 }} />
                <PolarRadiusAxis domain={[0, 100]} tick={false} />
                <Radar name="Score" dataKey="value" stroke={C.green} fill={C.green} fillOpacity={0.3} />
              </RadarChart>
            </ResponsiveContainer>
          </Body>
        </Card>
      </div>

      {/* Peak moments */}
      {peakMoments.length > 0 && (
        <Card>
          <Head label="? PEAK ENGAGEMENT MOMENTS � top 10%" accent={C.green} sub="Highest visual dynamics � ideal for thumbnails, highlights, teasers" />
          <Body>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(120px,1fr))', gap: 10 }}>
              {peakMoments.map((f, i) => {
                const frameIdx = Math.round(f.time / dur * frames.length);
                const frame = frames[Math.min(frames.length - 1, Math.max(0, frameIdx))];
                return frame ? (
                  <div key={i} style={{ position: 'relative', borderRadius: 4, overflow: 'hidden', border: `2px solid ${C.green}` }}>
                    <img src={frame.url} alt="" style={{ width: '100%', display: 'block' }} />
                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,.75)', padding: '4px 6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 8, color: '#fff', fontFamily: 'JetBrains Mono,monospace' }}>{fmtTime(f.time)}</span>
                      <EngageBadge score={f.engagement} />
                    </div>
                  </div>
                ) : null;
              })}
            </div>
          </Body>
        </Card>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// -- 14  COMPARE TAB ----------------------------------------------------------
// -----------------------------------------------------------------------------

function CompareTab({ state }) {
  const { timeline, frames, meta } = state;
  const dur = meta?.duration || 1;
  const [aRange, setARange] = useState([0, 0.4]);
  const [bRange, setBRange] = useState([0.6, 1.0]);

  if (!timeline.length) return <div style={{ padding: 60, textAlign: 'center', color: C.t4 }}>Load a video first.</div>;

  const getSegmentStats = (range) => {
    const start = range[0] * dur, end = range[1] * dur;
    const seg = timeline.filter(f => f.time >= start && f.time <= end);
    if (!seg.length) return null;
    const avg = (key) => seg.reduce((a, f) => a + f[key], 0) / seg.length;
    return {
      frames: seg.length,
      brightness:  +avg('brightness').toFixed(1),
      contrast:    +avg('contrast').toFixed(1),
      saturation:  +avg('saturation').toFixed(1),
      warmth:      +avg('warmth').toFixed(1),
      engagement:  +avg('engagement').toFixed(1),
      motion:      +avg('motionScore').toFixed(3),
      sharpness:   +avg('sharpness').toFixed(0),
      noise:       +avg('noiseLevel').toFixed(4),
    };
  };

  const A = getSegmentStats(aRange), B = getSegmentStats(bRange);
  const midFrame = (range) => {
    const mid = (range[0] + range[1]) / 2 * dur;
    return frames[Math.round(mid / dur * frames.length)] || frames[0];
  };

  const METRICS = [
    ['Brightness',  'brightness',  C.gold,   1,   255],
    ['Contrast',    'contrast',    C.orange,  0,   80],
    ['Saturation',  'saturation',  C.violet,  0,  100],
    ['Warmth',      'warmth',      C.orange,  0,  100],
    ['Engagement',  'engagement',  C.green,   0,  100],
    ['Sharpness',   'sharpness',   C.cyan,    0, null],
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Range pickers */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        {[['SEGMENT A', aRange, setARange, C.cyan], ['SEGMENT B', bRange, setBRange, C.violet]].map(([label, range, set, color]) => (
          <Card key={label} style={{ borderTop: `3px solid ${color}` }}>
            <Head label={label} accent={color} sub={`${fmtTime(range[0] * dur)} ? ${fmtTime(range[1] * dur)}`} />
            <Body>
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 9, color: C.t3, fontFamily: 'JetBrains Mono,monospace', marginBottom: 3 }}>START: {fmtTime(range[0] * dur)}</div>
                <input type="range" min={0} max={0.95} step={0.01} value={range[0]} onChange={e => set([+e.target.value, range[1]])} style={{ width: '100%', accentColor: color }} />
              </div>
              <div>
                <div style={{ fontSize: 9, color: C.t3, fontFamily: 'JetBrains Mono,monospace', marginBottom: 3 }}>END: {fmtTime(range[1] * dur)}</div>
                <input type="range" min={0.05} max={1} step={0.01} value={range[1]} onChange={e => set([range[0], +e.target.value])} style={{ width: '100%', accentColor: color }} />
              </div>
              {midFrame(range) && <img src={midFrame(range).url} alt="" style={{ width: '100%', marginTop: 10, borderRadius: 3 }} />}
            </Body>
          </Card>
        ))}
      </div>

      {/* Metric comparison */}
      {A && B && (
        <>
          <Card>
            <Head label="SEGMENT COMPARISON" accent={C.cyan} />
            <Body>
              {METRICS.map(([name, key, color, min, max]) => {
                const aV = A[key], bV = B[key];
                const range = (max ?? Math.max(aV, bV)) - min;
                const aPct  = range > 0 ? (aV - min) / range * 100 : 50;
                const bPct  = range > 0 ? (bV - min) / range * 100 : 50;
                const winner = aV > bV ? 'A' : aV < bV ? 'B' : '=';
                return (
                  <div key={name} style={{ marginBottom: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 9, color: C.t3, fontFamily: 'JetBrains Mono,monospace' }}>{name}</span>
                      <span style={{ fontSize: 9, color: color, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>
                        A: {typeof aV === 'number' && aV < 1 ? (aV * 1000).toFixed(1) + '�' : aV} vs B: {typeof bV === 'number' && bV < 1 ? (bV * 1000).toFixed(1) + '�' : bV}
                        <span style={{ marginLeft: 8, color: winner === '=' ? C.t4 : C.gold }}>{winner === 'A' ? '? A' : winner === 'B' ? '? B' : '='}</span>
                      </span>
                    </div>
                    <div style={{ position: 'relative', height: 14, background: C.deep, borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ position: 'absolute', top: 0, left: 0, height: '50%', width: `${Math.min(100, aPct)}%`, background: C.cyan, borderRadius: '3px 0 0 0', opacity: 0.85 }} />
                      <div style={{ position: 'absolute', bottom: 0, left: 0, height: '50%', width: `${Math.min(100, bPct)}%`, background: C.violet, borderRadius: '0 0 0 3px', opacity: 0.85 }} />
                    </div>
                  </div>
                );
              })}
            </Body>
          </Card>

          {/* Overlay chart */}
          <Card>
            <Head label="ENGAGEMENT � SEGMENT A vs B OVERLAY" accent={C.cyan} />
            <Body style={{ padding: 10 }}>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart margin={{ top: 4, right: 10, bottom: 10, left: 0 }}>
                  <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
                  <XAxis tick={{ fill: C.t4, fontSize: 8 }} />
                  <YAxis tick={{ fill: C.t4, fontSize: 8 }} domain={[0, 100]} />
                  <Tooltip content={<ChartTip />} />
                  <Legend wrapperStyle={{ fontSize: 9 }} />
                  <Line data={timeline.filter(f => f.time >= aRange[0] * dur && f.time <= aRange[1] * dur).map((f, i) => ({ i, v: f.engagement }))} type="monotone" dataKey="v" stroke={C.cyan}   dot={false} strokeWidth={2} isAnimationActive={false} name="Segment A" />
                  <Line data={timeline.filter(f => f.time >= bRange[0] * dur && f.time <= bRange[1] * dur).map((f, i) => ({ i, v: f.engagement }))} type="monotone" dataKey="v" stroke={C.violet} dot={false} strokeWidth={2} isAnimationActive={false} name="Segment B" />
                </LineChart>
              </ResponsiveContainer>
            </Body>
          </Card>
        </>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// -- 15  REPORT TAB -----------------------------------------------------------
// -----------------------------------------------------------------------------

const RPT_THEMES = {
  dark:  { bg:'#040810', surface:'#080f1c', card:'#0c1524', border:'#1a2a3a', t1:'#e2e8f0', t2:'#b6c3d8', t3:'#8396b3', t4:'#3a5060', accent:C.cyan, accent2:C.violet },
  light: { bg:'#f5f8ff', surface:'#ffffff', card:'#edf2ff', border:'#c8d5f0', t1:'#0a1520', t2:'#2a3f5a', t3:'#5a7090', t4:'#98b0c8', accent:'#0ea5e9', accent2:'#7c3aed' },
  noir:  { bg:'#060606', surface:'#0e0e0e', card:'#141414', border:'#2a2a2a', t1:'#f0f0f0', t2:'#c0c0c0', t3:'#808080', t4:'#404040', accent:'#e0e0e0', accent2:'#a0a0a0' },
};

function ReportTab({ state }) {
  const { timeline, cuts, shots, frames, meta, grade, audio, faceData, objectData } = state;
  const [theme, setTheme]   = useState('dark');
  const [title, setTitle]   = useState(meta?.name?.replace(/\.[^.]+$/, '') || 'Video Analysis Report');
  const T = RPT_THEMES[theme];

  if (!timeline.length) return <div style={{ padding: 60, textAlign: 'center', color: C.t4 }}>Load a video first.</div>;

  const dur       = meta?.duration || 0;
  const avgEngage = (timeline.reduce((a, f) => a + f.engagement, 0) / timeline.length).toFixed(1);
  const avgBright = (timeline.reduce((a, f) => a + f.brightness, 0) / timeline.length).toFixed(0);
  const avgSat    = (timeline.reduce((a, f) => a + f.saturation, 0) / timeline.length).toFixed(1);
  const avgWarmth = (timeline.reduce((a, f) => a + f.warmth, 0) / timeline.length).toFixed(1);
  const avgSharp  = (timeline.reduce((a, f) => a + f.sharpness, 0) / timeline.length / Math.max(...timeline.map(f => f.sharpness), 1) * 100).toFixed(0);
  const facePresence = faceData ? `${(faceData.filter(f => f.faces.length > 0).length / faceData.length * 100).toFixed(0)}%` : '�';
  const topObject = objectData ? Object.entries(objectData.reduce((a, f) => { f.objects.forEach(o => { a[o.class] = (a[o.class] || 0) + 1; }); return a; }, {})).sort((a, b) => b[1] - a[1])[0]?.[0] : '�';

  const exportMD = () => {
    const lines = [
      `# ${title}`, `> Generated ${new Date().toLocaleDateString()} � ${fmtTime(dur)} � ${meta?.width}�${meta?.height}`, '',
      '## Summary', `- **Grade Style:** ${grade?.style || '�'}`, `- **Avg Engagement:** ${avgEngage}/100`,
      `- **Avg Brightness:** ${avgBright}/255`, `- **Saturation:** ${avgSat}%`, `- **Warmth:** ${avgWarmth}%`,
      `- **Sharpness:** ${avgSharp}%`, '', '## Edit Structure',
      `- **Total Cuts:** ${cuts?.length || 0}`, `- **Total Shots:** ${shots?.length || 0}`,
      `- **Avg Shot Duration:** ${shots?.length ? (dur / shots.length).toFixed(1) : '�'}s`,
      `- **Pacing:** ${shots?.length ? (dur / shots.length < 3 ? 'RAPID' : dur / shots.length < 8 ? 'MODERATE' : 'SLOW') : '�'}`,
      '', '## Detections', `- **Face Presence:** ${facePresence}`, `- **Top Object Class:** ${topObject}`,
      '', '## Audio', `- **Silence Ratio:** ${audio?.silenceRatio || '�'}%`,
    ];
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([lines.join('\n')], { type: 'text/markdown' }));
    usageDone('video_intelligence', 'report_download', 1, { format: 'markdown' });
    a.download = `${title.replace(/\s/g, '-')}.md`; a.click();
  };

  const coverFrame = frames[Math.floor(frames.length / 2)];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Card>
        <Head label="REPORT SETTINGS" accent={C.cyan} />
        <Body>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, marginBottom: 12 }}>
            <div><Label>TITLE</Label><input value={title} onChange={e => setTitle(e.target.value)} style={{ width: '100%', background: C.deep, border: `1px solid ${C.b2}`, color: C.t1, padding: '8px 12px', fontSize: 13, borderRadius: 3, outline: 'none', boxSizing: 'border-box' }} /></div>
            <div><Label>THEME</Label><div style={{ display: 'flex', gap: 6 }}>{[['dark','? DARK'],['light','? LIGHT'],['noir','? NOIR']].map(([v, l]) => <button key={v} onClick={() => setTheme(v)} style={{ padding: '8px 14px', border: `1px solid ${theme === v ? C.cyan : C.b1}`, background: theme === v ? `${C.cyan}15` : 'transparent', color: theme === v ? C.cyan : C.t3, fontSize: 9, borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontWeight: theme === v ? 700 : 400 }}>{l}</button>)}</div></div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={exportMD} style={{ padding: '7px 16px', background: `${C.cyan}15`, border: `1px solid ${C.cyan}`, color: C.cyan, fontSize: 9, borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>?? MARKDOWN</button>
            <button onClick={() => window.print()} style={{ padding: '7px 16px', background: `${C.violet}15`, border: `1px solid ${C.violet}`, color: C.violet, fontSize: 9, borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>??? PRINT / PDF</button>
          </div>
        </Body>
      </Card>

      {/* Report */}
      <div style={{ background: T.bg, borderRadius: 8, border: `1px solid ${T.border}`, overflow: 'hidden', fontFamily: 'Georgia,serif' }}>
        {/* Cover */}
        <div style={{ display: 'grid', gridTemplateColumns: coverFrame ? '1fr 1fr' : '1fr', background: T.surface, padding: 0 }}>
          <div style={{ padding: '52px 48px', position: 'relative' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${T.accent},${T.accent2},transparent)` }} />
            <div style={{ fontSize: 9, letterSpacing: '.24em', color: T.accent, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, marginBottom: 12, opacity: .8 }}>FRAMESIGHT � CINEMATIC ANALYSIS</div>
            <h1 style={{ fontFamily: 'Syne,sans-serif', fontWeight: 900, fontSize: 28, color: T.t1, margin: '0 0 8px', lineHeight: 1.15 }}>{title}</h1>
            <div style={{ fontSize: 10, color: T.t3, fontFamily: 'JetBrains Mono,monospace', marginBottom: 24 }}>{meta?.width}�{meta?.height} � {fmtTime(dur)} � {meta?.size}MB</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {[
                ['GRADE', grade?.style || '�', T.accent],
                ['ENGAGEMENT', `${avgEngage}/100`, +avgEngage > 60 ? '#4ade80' : +avgEngage > 40 ? '#facc15' : '#f87171'],
                ['CUTS', cuts?.length || 0, T.accent2],
                ['SHARPNESS', `${avgSharp}%`, T.accent],
                ['WARMTH', `${avgWarmth}%`, '#fb923c'],
                ['SATURATION', `${avgSat}%`, '#a78bfa'],
              ].map(([l, v, c]) => (
                <div key={l} style={{ padding: '8px 10px', background: T.card, border: `1px solid ${T.border}`, borderRadius: 3 }}>
                  <div style={{ fontSize: 7, color: T.t4, fontFamily: 'JetBrains Mono,monospace', marginBottom: 2 }}>{l}</div>
                  <div style={{ fontSize: 15, fontFamily: 'Syne,sans-serif', fontWeight: 700, color: c }}>{v}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 18, fontSize: 9, color: T.t4, fontFamily: 'JetBrains Mono,monospace' }}>Generated {new Date().toLocaleString()} � jorvex.degenden.xyz</div>
          </div>
          {coverFrame && (
            <div style={{ position: 'relative', overflow: 'hidden', maxHeight: 380 }}>
              <img src={coverFrame.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', filter: 'brightness(.85)' }} />
              <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(90deg,${T.surface}20,transparent)` }} />
            </div>
          )}
        </div>

        {/* Sections */}
        <div style={{ padding: '36px 48px', borderTop: `1px solid ${T.border}`, background: T.bg }}>
          {/* Color timeline strip */}
          <div style={{ fontSize: 9, letterSpacing: '.18em', color: T.accent, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, marginBottom: 10 }}>COLOUR ARC</div>
          <div style={{ display: 'flex', height: 40, borderRadius: 4, overflow: 'hidden', marginBottom: 28 }}>
            {timeline.map((f, i) => <div key={i} style={{ flex: 1, background: f.hex, minWidth: 1 }} />)}
          </div>

          {/* Metrics grid */}
          <div style={{ fontSize: 9, letterSpacing: '.18em', color: T.accent, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, marginBottom: 14 }}>VISUAL METRICS</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 28 }}>
            {[
              ['Avg Brightness', `${avgBright}/255`, T.accent],
              ['Avg Contrast',   `${(timeline.reduce((a, f) => a + f.contrast, 0) / timeline.length).toFixed(1)}`, T.accent2],
              ['Avg Saturation', `${avgSat}%`, '#a78bfa'],
              ['Avg Warmth',     `${avgWarmth}%`, '#fb923c'],
              ['Avg Sharpness',  `${avgSharp}%`, T.accent],
              ['Avg Engagement', `${avgEngage}/100`, '#4ade80'],
              ['Face Presence',  facePresence, T.accent2],
              ['Top Object',     topObject || '�', T.accent],
            ].map(([l, v, c]) => (
              <div key={l} style={{ padding: '12px 14px', background: T.card, border: `1px solid ${T.border}`, borderRadius: 4 }}>
                <div style={{ fontSize: 8, color: T.t4, fontFamily: 'JetBrains Mono,monospace', marginBottom: 4 }}>{l}</div>
                <div style={{ fontSize: 16, fontFamily: 'Syne,sans-serif', fontWeight: 700, color: c }}>{v}</div>
              </div>
            ))}
          </div>

          {/* Grade interpretation */}
          {grade && (
            <>
              <div style={{ fontSize: 9, letterSpacing: '.18em', color: T.accent, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, marginBottom: 10 }}>CINEMATIC GRADE</div>
              <div style={{ padding: '16px 20px', background: T.card, border: `1px solid ${grade.color}44`, borderLeft: `4px solid ${grade.color}`, borderRadius: 4, marginBottom: 28 }}>
                <div style={{ fontSize: 20, fontFamily: 'Syne,sans-serif', fontWeight: 900, color: grade.color, marginBottom: 6 }}>{grade.style}</div>
                <div style={{ fontSize: 12, color: T.t2, lineHeight: 1.7 }}>{grade.desc}</div>
              </div>
            </>
          )}

          {/* Edit rhythm */}
          <div style={{ fontSize: 9, letterSpacing: '.18em', color: T.accent, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, marginBottom: 14 }}>EDIT STRUCTURE</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 28 }}>
            {[
              ['Total Shots',  shots?.length || 0, T.accent],
              ['Avg Shot Dur', shots?.length ? `${(dur / shots.length).toFixed(1)}s` : '�', T.accent2],
              ['Edit Pacing',  shots?.length ? (dur / shots.length < 3 ? 'RAPID' : dur / shots.length < 8 ? 'DYNAMIC' : 'CINEMATIC') : '�', T.accent],
            ].map(([l, v, c]) => (
              <div key={l} style={{ padding: '12px 14px', background: T.card, border: `1px solid ${T.border}`, borderRadius: 4, textAlign: 'center' }}>
                <div style={{ fontSize: 8, color: T.t4, fontFamily: 'JetBrains Mono,monospace', marginBottom: 4 }}>{l}</div>
                <div style={{ fontSize: 20, fontFamily: 'Syne,sans-serif', fontWeight: 800, color: c }}>{v}</div>
              </div>
            ))}
          </div>

          {/* Key frames */}
          {frames.length > 0 && (
            <>
              <div style={{ fontSize: 9, letterSpacing: '.18em', color: T.accent, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, marginBottom: 14 }}>KEY FRAMES</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: 8, marginBottom: 24 }}>
                {[0, 0.17, 0.33, 0.5, 0.67, 0.83].map(p => {
                  const f = frames[Math.round(p * (frames.length - 1))];
                  return f ? <div key={p} style={{ borderRadius: 3, overflow: 'hidden' }}><img src={f.url} alt="" style={{ width: '100%', display: 'block' }} /></div> : null;
                })}
              </div>
            </>
          )}
        </div>

        <div style={{ padding: '14px 48px', background: T.surface, borderTop: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 9, color: T.t4, fontFamily: 'JetBrains Mono,monospace' }}>jorvex.degenden.xyz � FRAMESIGHT</span>
          <span style={{ fontSize: 9, color: T.t4, fontFamily: 'JetBrains Mono,monospace' }}>{fmtTime(dur)} � {timeline.length} frames analysed � 100% in-browser</span>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// -- 16  STATE MANAGEMENT -----------------------------------------------------
// -----------------------------------------------------------------------------

const INIT = {
  videoSrc: null, frames: [], timeline: [], meta: null,
  cuts: null, shots: null, grade: null,
  audio: null, faceData: null, objectData: null,
};

function reducer(state, action) {
  switch (action.type) {
    case 'RESET':       return { ...INIT };
    case 'SET_SRC':     return { ...state, videoSrc: action.src };
    case 'SET_META':    return { ...state, meta: action.meta };
    case 'FULL_LOAD':   return { ...state, frames: action.frames, timeline: action.timeline, cuts: action.cuts, shots: action.shots, grade: action.grade, audio: action.audio };
    case 'FACE_DATA':   return { ...state, faceData: action.faceData };
    case 'OBJECT_DATA': return { ...state, objectData: action.objectData };
    default:            return state;
  }
}

// -----------------------------------------------------------------------------
// -- 17  ROOT COMPONENT -------------------------------------------------------
// -----------------------------------------------------------------------------

const TABS = [
  { id: 'upload',      icon: '?', label: 'UPLOAD',      color: C.cyan   },
  { id: 'timeline',    icon: '??', label: 'TIMELINE',    color: C.gold   },
  { id: 'composition', icon: '?', label: 'COMPOSE',     color: C.violet },
  { id: 'cuts',        icon: '?',  label: 'CUTS',        color: C.orange },
  { id: 'faces',       icon: '??', label: 'FACES',       color: C.pink   },
  { id: 'objects',     icon: '??', label: 'OBJECTS',     color: C.green  },
  { id: 'colorgrade',  icon: '??', label: 'COLOR GRADE', color: C.gold   },
  { id: 'audiosync',   icon: '??', label: 'AUDIO SYNC',  color: C.violet },
  { id: 'quality',     icon: '??', label: 'QUALITY',     color: C.cyan   },
  { id: 'engagement',  icon: '??', label: 'ENGAGEMENT',  color: C.green  },
  { id: 'compare',     icon: '?',  label: 'COMPARE',     color: C.cyan   },
  { id: 'report',      icon: '??', label: 'REPORT',      color: C.green  },
];

export default function FrameSight({ experiment, onUpdate, isAuthenticated }) {
  const [state, dispatch] = useReducer(reducer, INIT);
  const [tab, setTab]     = useState('upload');
  const workflowSteps = useMemo(() => usageWorkflowFromTabs(TABS), []);

  const hasVideo = !!state.timeline.length;

  const renderTab = () => {
    const p = { state, dispatch, isAuthenticated };
    switch (tab) {
      case 'upload':      return <UploadTab      {...p} />;
      case 'timeline':    return <TimelineTab    {...p} />;
      case 'composition': return <CompositionTab {...p} />;
      case 'cuts':        return <CutsTab        {...p} />;
      case 'faces':       return <FacesTab       {...p} />;
      case 'objects':     return <ObjectsTab     {...p} />;
      case 'colorgrade':  return <ColorGradeTab  {...p} />;
      case 'audiosync':   return <AudioSyncTab   {...p} />;
      case 'quality':     return <QualityTab     {...p} />;
      case 'engagement':  return <EngagementTab  {...p} />;
      case 'compare':     return <CompareTab     {...p} />;
      case 'report':      return <ReportTab      {...p} />;
      default:            return null;
    }
  };

  const statusDots = [
    ['F', !!state.faceData,   C.pink,  'Faces detected'],
    ['O', !!state.objectData, C.green, 'Objects detected'],
  ];

  useEffect(() => {
    if (!hasVideo || tab === 'upload' || !workflowSteps.includes(tab)) return;
    usageStep('video_intelligence', tab, { file_name: state.meta?.name || '' }, workflowSteps);
  }, [hasVideo, state.meta?.name, tab, workflowSteps]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 18px', background: C.deep, borderBottom: `1px solid ${C.b1}`, flexShrink: 0 }}>
        <div>
          <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 900, fontSize: 16, color: C.t1, lineHeight: 1 }}>FrameSight</div>
          <div style={{ fontSize: 8, color: C.t4, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.12em', marginTop: 2 }}>Cinematic video intelligence � 100% in-browser</div>
        </div>
        {hasVideo && state.meta && (
          <div style={{ fontSize: 9, color: C.t3, fontFamily: 'JetBrains Mono,monospace' }}>
            {state.meta.name} � {state.meta.width}�{state.meta.height} � {fmtTime(state.meta.duration)}
          </div>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {hasVideo && statusDots.map(([l, done, color, title]) => (
            <span key={l} title={title} style={{ fontSize: 9, padding: '2px 8px', background: done ? `${color}18` : C.raised, border: `1px solid ${done ? color : C.b1}`, borderRadius: 2, color: done ? color : C.t4, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{done ? '?' : '?'} {l}</span>
          ))}
          {hasVideo && state.grade && (
            <span style={{ fontSize: 9, padding: '2px 8px', background: `${state.grade.color}18`, border: `1px solid ${state.grade.color}`, borderRadius: 2, color: state.grade.color, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>? {state.grade.style}</span>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', background: C.surface, borderBottom: `1px solid ${C.b1}`, flexShrink: 0, overflowX: 'auto' }}>
        {TABS.map(t => {
          const active = tab === t.id;
          const locked = !hasVideo && t.id !== 'upload';
          return (
            <button key={t.id} onClick={() => !locked && setTab(t.id)}
              title={locked ? 'Load video first' : t.label}
              style={{ padding: '9px 12px', border: 'none', background: active ? C.raised : 'transparent', borderBottom: `2px solid ${active ? t.color : 'transparent'}`, color: active ? t.color : locked ? C.t4 + '55' : C.t3, fontSize: 8, cursor: locked ? 'not-allowed' : 'pointer', fontFamily: 'JetBrains Mono,monospace', fontWeight: active ? 700 : 400, letterSpacing: '.06em', whiteSpace: 'nowrap', transition: 'all .12s', display: 'flex', alignItems: 'center', gap: 4, opacity: locked ? 0.35 : 1 }}>
              <span style={{ fontSize: 10 }}>{t.icon}</span>{t.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px' }}>
        {renderTab()}
      </div>
    </div>
  );
}
